import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Zap, Download, Search, X } from 'lucide-react';
import { API_URL } from '../config';
import ScoreBadge from './shared/ScoreBadge';
import AIAnalysisPanel from './shared/AIAnalysisPanel';
import { formatCurrency, formatNumber } from '../utils/formatters';
import MetaOptimizationPanel from './MetaOptimizationPanel';
import InactiveAccountCardBase from './shared/InactiveAccountCard';
import { makeClientScorer } from './shared/scoring';
import usePerformanceData from '../hooks/usePerformanceData';

// Module scope: a new array each render would change the hook's deps and refetch forever.
const FALLBACK_RANGES = ['last_30_days'];

// ==================== TIMEFRAME OPTIONS ====================
const TIMEFRAME_OPTIONS = [
  { id: 'last_7_days', label: 'Last 7 Days', days: 7 },
  { id: 'last_14_days', label: 'Last 14 Days', days: 14 },
  { id: 'last_30_days', label: 'Last 30 Days', days: 30 },
  { id: 'last_90_days', label: 'Last 90 Days', days: 90 },
];

// ==================== SCORING SYSTEM ====================
const calculateCampaignScore = (campaign) => {
  let score = 50;
  const spend = campaign.spend || 0;
  const clicks = campaign.clicks || 0;
  const leads = campaign.leads || 0;
  const messaging = campaign.messaging_conversations || 0;
  const ctr = campaign.ctr || 0;
  const objective = campaign.objective || '';

  if (spend === 0) return { score: 0, status: 'inactive', reasons: ['No spend'] };

  const reasons = [];

  if (campaign.status === 'ACTIVE') score += 10;

  if (ctr >= 2) { score += 15; reasons.push('Strong CTR'); }
  else if (ctr >= 1) { score += 5; }
  else if (ctr < 0.5 && clicks > 0) { score -= 10; reasons.push('Low CTR'); }

  const isLeadCampaign = ['OUTCOME_LEADS', 'LEAD_GENERATION'].includes(objective);
  const isMessageCampaign = ['OUTCOME_ENGAGEMENT', 'MESSAGES', 'POST_ENGAGEMENT'].includes(objective);

  if (isLeadCampaign) {
    if (leads > 0) {
      const cpl = spend / leads;
      if (cpl <= 100) { score += 25; reasons.push(`Excellent CPL ₹${cpl.toFixed(0)}`); }
      else if (cpl <= 200) { score += 15; reasons.push(`Good CPL ₹${cpl.toFixed(0)}`); }
      else if (cpl <= 400) { score += 5; }
      else { score -= 10; reasons.push(`High CPL ₹${cpl.toFixed(0)}`); }
    } else {
      score -= 20;
      reasons.push('No leads generated');
    }
  } else if (isMessageCampaign) {
    if (messaging > 0) {
      const cpmc = spend / messaging;
      if (cpmc <= 50) { score += 25; reasons.push(`Excellent CPMC ₹${cpmc.toFixed(0)}`); }
      else if (cpmc <= 100) { score += 15; reasons.push(`Good CPMC ₹${cpmc.toFixed(0)}`); }
      else if (cpmc <= 200) { score += 5; }
      else { score -= 10; reasons.push(`High CPMC ₹${cpmc.toFixed(0)}`); }
    } else {
      score -= 15;
      reasons.push('No conversations started');
    }
  } else {
    if (clicks > 0) {
      const cpc = spend / clicks;
      if (cpc <= 5) { score += 20; reasons.push('Low CPC'); }
      else if (cpc <= 15) { score += 10; }
      else if (cpc > 30) { score -= 10; reasons.push('High CPC'); }
    }
  }

  const reach = campaign.reach || 0;
  const impressions = campaign.impressions || 0;
  if (reach > 0) {
    const frequency = impressions / reach;
    if (frequency > 4) { score -= 15; reasons.push('Creative fatigue (high frequency)'); }
    else if (frequency > 2.5) { score -= 5; reasons.push('Monitor frequency'); }
  }

  score = Math.max(0, Math.min(100, score));
  const status = score >= 70 ? 'good' : score >= 40 ? 'warning' : 'critical';

  return { score, status, reasons };
};

const calculateClientScore = makeClientScorer(calculateCampaignScore);
const ObjectiveBadge = ({ objective, campaignName }) => {
  const config = {
    'OUTCOME_LEADS': { name: 'Lead Form', color: 'bg-blue-500/20 text-blue-400', icon: '📝' },
    'LEAD_GENERATION': { name: 'Lead Form', color: 'bg-blue-500/20 text-blue-400', icon: '📝' },
    'OUTCOME_ENGAGEMENT': { name: 'WhatsApp', color: 'bg-green-500/20 text-green-400', icon: '💬' },
    'MESSAGES': { name: 'Messages', color: 'bg-green-500/20 text-green-400', icon: '💬' },
    'POST_ENGAGEMENT': { name: 'Engagement', color: 'bg-green-500/20 text-green-400', icon: '💬' },
    'OUTCOME_TRAFFIC': { name: 'Traffic', color: 'bg-purple-500/20 text-purple-400', icon: '🔗' },
    'LINK_CLICKS': { name: 'Traffic', color: 'bg-purple-500/20 text-purple-400', icon: '🔗' },
    'OUTCOME_SALES': { name: 'Sales', color: 'bg-orange-500/20 text-orange-400', icon: '🛒' },
    'CONVERSIONS': { name: 'Conversions', color: 'bg-orange-500/20 text-orange-400', icon: '🛒' },
    'OUTCOME_AWARENESS': { name: 'Awareness', color: 'bg-cyan-500/20 text-cyan-400', icon: '📢' },
    'REACH': { name: 'Reach', color: 'bg-cyan-500/20 text-cyan-400', icon: '📢' },
  };

  // If no objective, try to detect from campaign name
  let cfg = config[objective];
  if (!cfg && campaignName) {
    const detectedType = detectCampaignTypeFromName(campaignName);
    if (detectedType === 'messaging') {
      cfg = { name: 'WhatsApp', color: 'bg-green-500/20 text-green-400', icon: '💬' };
    } else if (detectedType === 'sales') {
      cfg = { name: 'Sales', color: 'bg-orange-500/20 text-orange-400', icon: '🛒' };
    } else if (detectedType === 'traffic') {
      cfg = { name: 'Traffic', color: 'bg-purple-500/20 text-purple-400', icon: '🔗' };
    } else if (detectedType === 'awareness') {
      cfg = { name: 'Awareness', color: 'bg-cyan-500/20 text-cyan-400', icon: '📢' };
    }
  }
  
  cfg = cfg || { name: objective?.replace('OUTCOME_', '') || 'Campaign', color: 'bg-zinc-500/20 text-zinc-400', icon: null };

  return (
    <span className={`${cfg.color} px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1`}>
      <span>{cfg.icon}</span>
      <span>{cfg.name}</span>
    </span>
  );
};

// AI Analysis Panel
const detectCampaignTypeFromName = (campaignName) => {
  const name = (campaignName || '').toLowerCase();
  // WhatsApp/Messaging indicators
  if (name.includes(' wa') || name.endsWith(' wa') || name.includes('whatsapp') || 
      name.includes('messenger') || name.includes('messaging') || name.includes('-wa-') ||
      name.includes('_wa_') || name.includes(' wa ')) {
    return 'messaging';
  }
  // Sales/Catalog indicators
  if (name.includes('catalog') || name.includes('shopping') || name.includes('sales') ||
      name.includes('purchase') || name.includes('conversion')) {
    return 'sales';
  }
  // Traffic indicators
  if (name.includes('traffic') || name.includes('click') || name.includes('website')) {
    return 'traffic';
  }
  // Awareness indicators
  if (name.includes('awareness') || name.includes('reach') || name.includes('video view')) {
    return 'awareness';
  }
  return null;
};

const getCollapsedKPIs = (campaign) => {
  const objective = campaign.objective || '';
  const frequency = campaign.reach > 0 ? (campaign.impressions / campaign.reach) : 0;
  const freqStatus = frequency > 3 ? 'critical' : frequency > 2 ? 'warning' : frequency > 1.5 ? 'watch' : 'good';
  
  // Detect campaign type from name if objective is missing
  const detectedType = !objective ? detectCampaignTypeFromName(campaign.name) : null;

  // Lead Generation campaigns - Focus on CPL, Lead Rate, CTR, LP View Rate
  if (['OUTCOME_LEADS', 'LEAD_GENERATION'].includes(objective)) {
    const leads = campaign.leads || 0;
    const cpl = leads > 0 ? campaign.spend / leads : 0;
    const clickToLeadRate = campaign.clicks > 0 ? ((leads / campaign.clicks) * 100) : 0;
    // eslint-disable-next-line no-unused-vars
    const lpvRate = campaign.clicks > 0 ? ((campaign.landing_page_views || 0) / campaign.clicks * 100) : 0;
    
    return [
      { label: 'CPL', value: formatCurrency(cpl), status: cpl > 0 ? (cpl <= 100 ? 'good' : cpl <= 200 ? 'warning' : 'critical') : 'normal', highlight: true },
      { label: 'Lead Rate', value: `${clickToLeadRate.toFixed(1)}%`, status: clickToLeadRate >= 5 ? 'good' : clickToLeadRate >= 2 ? 'warning' : 'critical' },
      { label: 'CTR', value: `${(campaign.ctr || 0).toFixed(2)}%`, status: campaign.ctr >= 1.5 ? 'good' : campaign.ctr >= 0.8 ? 'warning' : 'critical' },
      { label: 'Freq', value: `${frequency.toFixed(1)}x`, status: freqStatus },
    ];
  }

  // WhatsApp/Messaging campaigns - Focus on CPMC, Message Rate, CTR
  // Also detect by name if objective is missing
  if (['OUTCOME_ENGAGEMENT', 'MESSAGES', 'POST_ENGAGEMENT'].includes(objective) || detectedType === 'messaging') {
    const messages = campaign.messaging_conversations || 0;
    const cpmc = messages > 0 ? campaign.spend / messages : 0;
    const messageRate = campaign.clicks > 0 ? ((messages / campaign.clicks) * 100) : 0;
    
    return [
      { label: 'CPMC', value: formatCurrency(cpmc), status: cpmc > 0 ? (cpmc <= 50 ? 'good' : cpmc <= 100 ? 'warning' : 'critical') : 'normal', highlight: true },
      { label: 'Msg Rate', value: `${messageRate.toFixed(1)}%`, status: messageRate >= 10 ? 'good' : messageRate >= 5 ? 'warning' : 'critical' },
      { label: 'CTR', value: `${(campaign.ctr || 0).toFixed(2)}%`, status: campaign.ctr >= 1.5 ? 'good' : campaign.ctr >= 0.8 ? 'warning' : 'critical' },
      { label: 'Freq', value: `${frequency.toFixed(1)}x`, status: freqStatus },
    ];
  }

  // Sales/Conversion campaigns - Focus on ROAS, CPA, Conv Rate
  // Also detect by name if objective is missing
  if (['OUTCOME_SALES', 'CONVERSIONS', 'PRODUCT_CATALOG_SALES'].includes(objective) || detectedType === 'sales') {
    const purchases = campaign.purchases || 0;
    const cpa = purchases > 0 ? campaign.spend / purchases : 0;
    const roas = campaign.roas || 0;
    const convRate = campaign.clicks > 0 ? ((purchases / campaign.clicks) * 100) : 0;
    
    return [
      { label: 'ROAS', value: `${roas.toFixed(2)}x`, status: roas >= 3 ? 'good' : roas >= 1.5 ? 'warning' : 'critical', highlight: true },
      { label: 'CPA', value: formatCurrency(cpa), status: cpa > 0 ? (cpa <= 200 ? 'good' : cpa <= 500 ? 'warning' : 'critical') : 'normal' },
      { label: 'Conv %', value: `${convRate.toFixed(1)}%`, status: convRate >= 3 ? 'good' : convRate >= 1 ? 'warning' : 'critical' },
      { label: 'Freq', value: `${frequency.toFixed(1)}x`, status: freqStatus },
    ];
  }

  // Traffic campaigns - Focus on CPC, CTR, LP View Rate
  // Also detect by name if objective is missing
  if (['OUTCOME_TRAFFIC', 'LINK_CLICKS', 'WEBSITE_TRAFFIC'].includes(objective) || detectedType === 'traffic') {
    const lpvRate = campaign.clicks > 0 ? ((campaign.landing_page_views || 0) / campaign.clicks * 100) : 0;
    
    return [
      { label: 'CPC', value: formatCurrency(campaign.cpc), status: campaign.cpc <= 5 ? 'good' : campaign.cpc <= 15 ? 'warning' : 'critical', highlight: true },
      { label: 'CTR', value: `${(campaign.ctr || 0).toFixed(2)}%`, status: campaign.ctr >= 2 ? 'good' : campaign.ctr >= 1 ? 'warning' : 'critical' },
      { label: 'LP View %', value: `${lpvRate.toFixed(0)}%`, status: lpvRate >= 70 ? 'good' : lpvRate >= 50 ? 'warning' : 'critical' },
      { label: 'Freq', value: `${frequency.toFixed(1)}x`, status: freqStatus },
    ];
  }

  // Awareness campaigns - Focus on CPP, CPM, Reach
  // Also detect by name if objective is missing
  if (['OUTCOME_AWARENESS', 'BRAND_AWARENESS', 'REACH', 'VIDEO_VIEWS'].includes(objective) || detectedType === 'awareness') {
    const cpp = campaign.reach > 0 ? (campaign.spend / campaign.reach * 1000) : 0;
    
    return [
      { label: 'CPP (1K)', value: formatCurrency(cpp), status: cpp <= 50 ? 'good' : cpp <= 100 ? 'warning' : 'critical', highlight: true },
      { label: 'CPM', value: formatCurrency(campaign.cpm), status: campaign.cpm <= 100 ? 'good' : campaign.cpm <= 200 ? 'warning' : 'critical' },
      { label: 'Reach', value: formatNumber(campaign.reach), status: 'normal' },
      { label: 'Freq', value: `${frequency.toFixed(1)}x`, status: freqStatus },
    ];
  }

  // App Install campaigns - Focus on CPI, Install Rate
  if (['OUTCOME_APP_PROMOTION', 'APP_INSTALLS'].includes(objective)) {
    const installs = campaign.app_installs || 0;
    const cpi = installs > 0 ? campaign.spend / installs : 0;
    const installRate = campaign.clicks > 0 ? ((installs / campaign.clicks) * 100) : 0;
    
    return [
      { label: 'CPI', value: formatCurrency(cpi), status: cpi > 0 ? (cpi <= 30 ? 'good' : cpi <= 80 ? 'warning' : 'critical') : 'normal', highlight: true },
      { label: 'Install %', value: `${installRate.toFixed(1)}%`, status: installRate >= 15 ? 'good' : installRate >= 8 ? 'warning' : 'critical' },
      { label: 'CTR', value: `${(campaign.ctr || 0).toFixed(2)}%`, status: campaign.ctr >= 1.5 ? 'good' : campaign.ctr >= 0.8 ? 'warning' : 'critical' },
      { label: 'Freq', value: `${frequency.toFixed(1)}x`, status: freqStatus },
    ];
  }

  // Default - generic metrics
  return [
    { label: 'CPC', value: formatCurrency(campaign.cpc), status: 'normal', highlight: true },
    { label: 'CPM', value: formatCurrency(campaign.cpm), status: 'normal' },
    { label: 'CTR', value: `${(campaign.ctr || 0).toFixed(2)}%`, status: campaign.ctr >= 1.5 ? 'good' : campaign.ctr >= 0.8 ? 'warning' : 'critical' },
    { label: 'Freq', value: `${frequency.toFixed(1)}x`, status: freqStatus },
  ];
};

// Get objective-specific secondary stats for collapsed view
const getObjectiveSecondaryStats = (campaign) => {
  const objective = campaign.objective || '';
  
  // Detect campaign type from name if objective is missing
  const detectedType = !objective ? detectCampaignTypeFromName(campaign.name) : null;
  
  // Lead campaigns - show leads generated, click-to-lead rate
  if (['OUTCOME_LEADS', 'LEAD_GENERATION'].includes(objective)) {
    const leads = campaign.leads || 0;
    const clickToLeadRate = campaign.clicks > 0 ? ((leads / campaign.clicks) * 100) : 0;
    return `${formatNumber(campaign.clicks)} clicks → ${leads} leads (${clickToLeadRate.toFixed(1)}% conv)`;
  }
  
  // Messaging campaigns - show messages started, click-to-message rate
  // Also detect by name if objective is missing
  if (['OUTCOME_ENGAGEMENT', 'MESSAGES', 'POST_ENGAGEMENT'].includes(objective) || detectedType === 'messaging') {
    const messages = campaign.messaging_conversations || 0;
    const messageRate = campaign.clicks > 0 ? ((messages / campaign.clicks) * 100) : 0;
    return `${formatNumber(campaign.clicks)} clicks → ${messages} msgs (${messageRate.toFixed(1)}% conv)`;
  }
  
  // Sales campaigns - show purchases, revenue
  // Also detect by name if objective is missing
  if (['OUTCOME_SALES', 'CONVERSIONS', 'PRODUCT_CATALOG_SALES'].includes(objective) || detectedType === 'sales') {
    const purchases = campaign.purchases || 0;
    const revenue = campaign.purchase_value || 0;
    return `${formatNumber(campaign.clicks)} clicks → ${purchases} purchases${revenue > 0 ? ` (₹${formatNumber(revenue)})` : ''}`;
  }
  
  // Traffic campaigns - show clicks, landing page views
  // Also detect by name if objective is missing
  if (['OUTCOME_TRAFFIC', 'LINK_CLICKS', 'WEBSITE_TRAFFIC'].includes(objective) || detectedType === 'traffic') {
    const lpv = campaign.landing_page_views || 0;
    const lpvRate = campaign.clicks > 0 ? ((lpv / campaign.clicks) * 100) : 0;
    return `${formatNumber(campaign.clicks)} clicks → ${formatNumber(lpv)} LP views (${lpvRate.toFixed(0)}% rate)`;
  }
  
  // Awareness campaigns - show reach vs impressions
  // Also detect by name if objective is missing
  if (['OUTCOME_AWARENESS', 'BRAND_AWARENESS', 'REACH', 'VIDEO_VIEWS'].includes(objective) || detectedType === 'awareness') {
    const frequency = campaign.reach > 0 ? (campaign.impressions / campaign.reach) : 0;
    return `${formatNumber(campaign.reach)} unique reach • ${formatNumber(campaign.impressions)} impressions • ${frequency.toFixed(1)}x frequency`;
  }
  
  // Default
  return `${formatNumber(campaign.impressions)} impr • ${formatNumber(campaign.reach)} reach • CTR ${campaign.ctr?.toFixed(2) || 0}%`;
};

// Campaign Row Component - Simplified without expansion
const CampaignRow = ({ campaign }) => {
  const scoreData = calculateCampaignScore(campaign);
  
  // Detect campaign type from name if objective is missing
  const detectedType = !campaign.objective ? detectCampaignTypeFromName(campaign.name) : null;
  
  const isLeadCampaign = ['OUTCOME_LEADS', 'LEAD_GENERATION'].includes(campaign.objective);
  const isMessageCampaign = ['OUTCOME_ENGAGEMENT', 'MESSAGES', 'POST_ENGAGEMENT'].includes(campaign.objective) || detectedType === 'messaging';
  const isSalesCampaign = ['OUTCOME_SALES', 'CONVERSIONS', 'PRODUCT_CATALOG_SALES'].includes(campaign.objective) || detectedType === 'sales';
  
  // Calculate frequency
  const frequency = campaign.reach > 0 ? (campaign.impressions / campaign.reach) : 0;
  const freqStatus = frequency > 3 ? 'critical' : frequency > 2 ? 'warning' : frequency > 1.5 ? 'watch' : 'good';

  // Dynamic primary metric based on objective (or detected type)
  const primaryMetric = isLeadCampaign 
    ? { label: 'Leads', value: campaign.leads || 0 }
    : isMessageCampaign 
    ? { label: 'Messages', value: campaign.messaging_conversations || 0 }
    : isSalesCampaign
    ? { label: 'Purchases', value: campaign.purchases || 0 }
    : { label: 'Clicks', value: campaign.clicks || 0 };

  // Dynamic cost metric based on objective (or detected type)
  const costMetric = isLeadCampaign && campaign.leads > 0
    ? { label: 'CPL', value: campaign.spend / campaign.leads }
    : isMessageCampaign && campaign.messaging_conversations > 0
    ? { label: 'CPMC', value: campaign.spend / campaign.messaging_conversations }
    : isSalesCampaign && campaign.purchases > 0
    ? { label: 'CPA', value: campaign.spend / campaign.purchases }
    : { label: 'CPC', value: campaign.cpc || 0 };

  // Get objective-specific KPIs for collapsed view
  const collapsedKPIs = getCollapsedKPIs(campaign);
  const secondaryStats = getObjectiveSecondaryStats(campaign);

  return (
    <div className={`bg-[#0D0D0F] rounded-xl border transition-colors ${
      scoreData.status === 'good' ? 'border-emerald-500/20 hover:border-emerald-500/40' :
      scoreData.status === 'warning' ? 'border-amber-500/20 hover:border-amber-500/40' :
      scoreData.status === 'critical' ? 'border-red-500/20 hover:border-red-500/40' :
      'border-white/10 hover:border-white/20'
    }`}>
      <div className="p-3 sm:p-4">
        {/* Mobile Layout */}
        <div className="sm:hidden">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <ObjectiveBadge objective={campaign.objective} campaignName={campaign.name} />
            <ScoreBadge score={scoreData.score} status={scoreData.status} size="sm" />
            {campaign.health_score !== undefined && campaign.health_score !== null && (
              <span className={`text-xs font-mono px-2 py-0.5 rounded-full font-bold ${
                campaign.health_score >= 70 ? 'bg-green-500/15 text-green-400' :
                campaign.health_score >= 40 ? 'bg-yellow-500/15 text-yellow-400' :
                'bg-red-500/15 text-red-400'
              }`}>
                {Math.round(campaign.health_score)}%
              </span>
            )}
            {campaign.status !== 'ACTIVE' && (
              <span className="px-2 py-0.5 bg-zinc-500/20 text-zinc-400 rounded-md text-[10px] font-bold">
                {campaign.status}
              </span>
            )}
          </div>
          <h4 className="text-sm font-semibold text-white truncate mb-3">{campaign.name}</h4>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="bg-[#18181B] rounded-lg p-2 text-center">
              <p className="text-[9px] text-zinc-500 uppercase">{primaryMetric.label}</p>
              <p className="text-sm font-bold text-emerald-400 font-mono">{formatNumber(primaryMetric.value)}</p>
            </div>
            <div className="bg-[#18181B] rounded-lg p-2 text-center">
              <p className="text-[9px] text-zinc-500 uppercase">{costMetric.label}</p>
              <p className="text-sm font-bold text-white font-mono">{formatCurrency(costMetric.value)}</p>
            </div>
            <div className="bg-[#18181B] rounded-lg p-2 text-center">
              <p className="text-[9px] text-zinc-500 uppercase">Spend</p>
              <p className="text-sm font-bold text-white font-mono">{formatCurrency(campaign.spend)}</p>
            </div>
          </div>
          {/* Mobile KPI bar */}
          <div className="grid grid-cols-4 gap-1">
            {collapsedKPIs.map((kpi, idx) => (
              <div 
                key={idx}
                className={`text-center py-1.5 px-1 rounded-lg ${
                  kpi.status === 'critical' ? 'bg-red-500/10' :
                  kpi.status === 'warning' ? 'bg-amber-500/10' :
                  kpi.status === 'good' ? 'bg-emerald-500/10' :
                  kpi.status === 'watch' ? 'bg-yellow-500/10' :
                  'bg-white/5'
                }`}
              >
                <p className={`text-[10px] font-bold font-mono ${
                  kpi.status === 'critical' ? 'text-red-400' :
                  kpi.status === 'warning' ? 'text-amber-400' :
                  kpi.status === 'good' ? 'text-emerald-400' :
                  kpi.status === 'watch' ? 'text-yellow-400' :
                  'text-white'
                }`}>
                  {kpi.value}
                </p>
                <p className="text-[8px] text-zinc-500 uppercase">{kpi.label}</p>
              </div>
            ))}
          </div>
        </div>
        
        {/* Desktop Layout */}
        <div className="hidden sm:block">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <ObjectiveBadge objective={campaign.objective} campaignName={campaign.name} />
                <ScoreBadge score={scoreData.score} status={scoreData.status} size="sm" />
                {campaign.health_score !== undefined && campaign.health_score !== null && (
                  <span className={`text-xs font-mono px-2 py-0.5 rounded-full font-bold ${
                    campaign.health_score >= 70 ? 'bg-green-500/15 text-green-400' :
                    campaign.health_score >= 40 ? 'bg-yellow-500/15 text-yellow-400' :
                    'bg-red-500/15 text-red-400'
                  }`}>
                    {Math.round(campaign.health_score)}%
                  </span>
                )}
                {campaign.status !== 'ACTIVE' && (
                  <span className="px-2 py-0.5 bg-zinc-500/20 text-zinc-400 rounded-md text-[10px] font-bold">
                    {campaign.status}
                  </span>
                )}
                {/* Frequency Badge - Color-coded based on status */}
                <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${
                  freqStatus === 'critical' ? 'bg-red-500/20 text-red-400' :
                  freqStatus === 'warning' ? 'bg-amber-500/20 text-amber-400' :
                  freqStatus === 'watch' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-zinc-500/20 text-zinc-400'
                }`}>
                  {frequency.toFixed(1)}x freq
                </span>
              </div>
              <h4 className="text-sm font-semibold text-white truncate">{campaign.name}</h4>
              {/* Objective-specific secondary stats */}
              <div className="flex items-center gap-3 mt-2 text-xs text-zinc-500">
                {secondaryStats}
              </div>
            </div>
            <div className="flex items-center gap-3 md:gap-4 text-right shrink-0">
              <div className="text-center min-w-[45px] md:min-w-[50px]">
                <p className="text-[10px] text-zinc-500 uppercase">{primaryMetric.label}</p>
                <p className="text-base md:text-lg font-bold text-emerald-400 font-mono">{formatNumber(primaryMetric.value)}</p>
              </div>
              <div className="text-center min-w-[50px] md:min-w-[60px]">
                <p className="text-[10px] text-zinc-500 uppercase">{costMetric.label}</p>
                <p className="text-base md:text-lg font-bold text-white font-mono">{formatCurrency(costMetric.value)}</p>
              </div>
              <div className="text-center min-w-[55px] md:min-w-[70px]">
                <p className="text-[10px] text-zinc-500 uppercase">Spend</p>
                <p className="text-base md:text-lg font-bold text-white font-mono">{formatCurrency(campaign.spend)}</p>
              </div>
            </div>
          </div>
          
          {/* Objective-specific KPI bar */}
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
            {collapsedKPIs.map((kpi, idx) => (
              <div 
                key={idx}
                className={`flex-1 text-center py-1.5 px-2 rounded-lg ${
                  kpi.status === 'critical' ? 'bg-red-500/10' :
                  kpi.status === 'warning' ? 'bg-amber-500/10' :
                  kpi.status === 'good' ? 'bg-emerald-500/10' :
                  kpi.status === 'watch' ? 'bg-yellow-500/10' :
                  'bg-white/5'
                }`}
              >
                <p className={`text-xs font-bold font-mono ${
                  kpi.status === 'critical' ? 'text-red-400' :
                  kpi.status === 'warning' ? 'text-amber-400' :
                  kpi.status === 'good' ? 'text-emerald-400' :
                  kpi.status === 'watch' ? 'text-yellow-400' :
                  'text-white'
                }`}>
                  {kpi.value}
                </p>
                <p className="text-[9px] text-zinc-500 uppercase mt-0.5">{kpi.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Client Card Component
const ClientCard = ({ client, campaigns, isExpanded, onToggle, onAnalyze, addToast }) => {
  const clientScore = calculateClientScore(campaigns);
  
  // Use account-level metrics if available (from API), fallback to campaign aggregation
  const activeCampaigns = campaigns.filter(c => c.spend > 0);
  const dormantCampaigns = campaigns.filter(c => c.spend === 0 || c.spend === null);

  // Prefer account-level metrics from API
  const totalSpend = client.metrics?.spend || activeCampaigns.reduce((sum, c) => sum + (c.spend || 0), 0);
  const totalLeads = client.metrics?.leads || activeCampaigns.reduce((sum, c) => sum + (c.leads || 0), 0);
  // FIXED: Only count messaging_conversations from actual messaging campaigns
  const totalMessages = client.metrics?.messaging_leads || activeCampaigns.reduce((sum, c) => {
    const isMessagingCampaign = ['OUTCOME_ENGAGEMENT', 'MESSAGES', 'POST_ENGAGEMENT'].includes(c.objective);
    return sum + (isMessagingCampaign ? (c.messaging_conversations || 0) : 0);
  }, 0);
  // Also count purchases from sales campaigns
  const totalPurchases = activeCampaigns.reduce((sum, c) => {
    const isSalesCampaign = ['OUTCOME_SALES', 'CONVERSIONS', 'PRODUCT_CATALOG_SALES'].includes(c.objective);
    return sum + (isSalesCampaign ? (c.purchases || 0) : 0);
  }, 0);
  const totalResults = totalLeads + totalMessages + totalPurchases;
  const avgCPR = totalResults > 0 ? totalSpend / totalResults : 0;

  // Count campaigns with spend for display
  const campaignsWithSpend = campaigns.filter(c => c.spend && c.spend > 0);
  const goodCount = campaignsWithSpend.filter(c => calculateCampaignScore(c).status === 'good').length;
  const warningCount = campaignsWithSpend.filter(c => calculateCampaignScore(c).status === 'warning').length;
  const criticalCount = campaignsWithSpend.filter(c => calculateCampaignScore(c).status === 'critical').length;

  // Team assignment info
  const assignedMarketerName = client.assigned_marketer_name;
  const teamLeadName = client.team_lead_name;

  return (
    <div className={`bg-[#09090B] rounded-xl sm:rounded-2xl border transition-colors ${
      isExpanded ? 'border-[#1877F2]/50 ring-2 ring-[#1877F2]/20' :
      clientScore.status === 'good' ? 'border-emerald-500/30 hover:border-emerald-500/50' :
      clientScore.status === 'warning' ? 'border-amber-500/30 hover:border-amber-500/50' :
      clientScore.status === 'critical' ? 'border-red-500/30 hover:border-red-500/50' :
      'border-white/10 hover:border-white/20'
    }`}>
      <div className="p-3 sm:p-5 cursor-pointer" onClick={onToggle}>
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#1877F2] rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg shadow-[#1877F2]/20 flex-shrink-0">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                <h3 className="font-bold text-white text-sm sm:text-lg truncate max-w-[120px] sm:max-w-none">{client.name}</h3>
                <ScoreBadge score={clientScore.score} status={clientScore.status} />
              </div>
              {/* Desktop team tags */}
              <div className="hidden sm:flex items-center gap-2 mt-1 flex-wrap">
                {assignedMarketerName && (
                  <span className="bg-emerald-500 text-black px-2 py-0.5 rounded-lg text-[10px] font-bold flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {assignedMarketerName.trim()}
                  </span>
                )}
                {teamLeadName && teamLeadName !== 'None' && (
                  <span className="bg-[#6366f1] text-black px-2 py-0.5 rounded-lg text-[10px] font-bold flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    {teamLeadName}
                  </span>
                )}
              </div>
              <div className="hidden sm:flex items-center gap-2 mt-1 text-xs text-zinc-500">
                <span>{campaignsWithSpend.length > 0 ? `${campaignsWithSpend.length} active campaigns` : `${campaigns.length} campaigns`}</span>
                {dormantCampaigns.length > 0 && campaignsWithSpend.length > 0 && (
                  <span className="text-zinc-600">• {dormantCampaigns.length} dormant</span>
                )}
                {(goodCount > 0 || warningCount > 0 || criticalCount > 0) && <span className="text-zinc-700">|</span>}
                {goodCount > 0 && <span className="text-emerald-400">{goodCount} good</span>}
                {warningCount > 0 && <span className="text-amber-400">{warningCount} warning</span>}
                {criticalCount > 0 && <span className="text-red-400">{criticalCount} critical</span>}
              </div>
              {/* Mobile campaign count */}
              <div className="flex sm:hidden items-center gap-2 mt-1 text-[10px] text-zinc-500">
                <span>{campaignsWithSpend.length} active</span>
                {goodCount > 0 && <span className="text-emerald-400">• {goodCount}✓</span>}
                {criticalCount > 0 && <span className="text-red-400">• {criticalCount}!</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-6 flex-shrink-0">
            <div className="hidden md:flex items-center gap-4 lg:gap-6">
              <div className="text-right">
                <p className="text-xs text-zinc-500 uppercase">Spend</p>
                <p className="text-lg lg:text-xl font-bold text-white font-mono">{formatCurrency(totalSpend)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-zinc-500 uppercase">Results</p>
                <p className="text-lg lg:text-xl font-bold text-emerald-400 font-mono">{formatNumber(totalResults)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-zinc-500 uppercase">Avg CPR</p>
                <p className="text-lg lg:text-xl font-bold text-white font-mono">{formatCurrency(avgCPR)}</p>
              </div>
            </div>
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center transition-colors ${
              isExpanded ? 'bg-[#1877F2]/20 text-[#1877F2]' : 'bg-white/5 text-zinc-500'
            }`}>
              <svg className={`w-4 h-4 sm:w-5 sm:h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        {/* Mobile/Tablet Metrics Grid */}
        <div className="grid grid-cols-3 gap-2 mt-3 md:hidden">
          <div className="bg-white/5 rounded-lg p-2 text-center">
            <p className="text-[10px] text-zinc-500">Spend</p>
            <p className="text-xs sm:text-sm font-bold text-white font-mono">{formatCurrency(totalSpend)}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-2 text-center">
            <p className="text-[10px] text-zinc-500">Results</p>
            <p className="text-xs sm:text-sm font-bold text-emerald-400 font-mono">{formatNumber(totalResults)}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-2 text-center">
            <p className="text-[10px] text-zinc-500">Avg CPR</p>
            <p className="text-xs sm:text-sm font-bold text-white font-mono">{formatCurrency(avgCPR)}</p>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-white/10 p-5 animate-fade-in">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-bold text-white flex items-center gap-2">
              <svg className="w-4 h-4 text-[#1877F2]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Campaign Performance
            </h4>
            <button
              onClick={(e) => { e.stopPropagation(); onAnalyze(); }}
              className="px-3 py-1.5 bg-gradient-to-r from-[#1877F2] to-[#6366f1] text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 hover:opacity-90 transition-colors"
            >
              <Zap className="w-4 h-4" />
              AI Analysis
            </button>
          </div>

          <div className="space-y-3 max-h-[500px] overflow-y-auto custom-scrollbar pr-1">
            {activeCampaigns
              .sort((a, b) => (b.spend || 0) - (a.spend || 0))
              .map(campaign => (
                <CampaignRow
                  key={campaign.id}
                  campaign={campaign}
                />
              ))}
          </div>

          {activeCampaigns.length === 0 && (
            <div className="text-center py-8 text-zinc-500">
              <p>No active campaigns with spend</p>
            </div>
          )}

          <div className="mt-4 border-t border-[#27272a] pt-4">
            <p className="text-xs text-[#71717a] uppercase tracking-wider mb-3" style={{fontFamily:'Manrope,sans-serif'}}>AI Optimization</p>
            <MetaOptimizationPanel
              accountId={client.id}
              accountName={client.name}
              addToast={addToast}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Inactive Account Card - FIXED to show account name
const InactiveAccountCard = ({ account }) => (
  <InactiveAccountCardBase
    account={account}
    idPrefix="act_"
    logoWrapperClassName="w-12 h-12 bg-[#1877F2] rounded-xl flex items-center justify-center shadow-lg shadow-[#1877F2]/20 opacity-50"
    logo={
      <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    }
    buttonClassName="bg-[#1877F2]/20 hover:bg-[#1877F2]/30 text-[#1877F2]"
    buttonLabel="Open in Meta Ads"
    buildUrl={(id) => `https://business.facebook.com/adsmanager/manage/campaigns?act=${id}`}
  />
);

const MetaPerformance = ({ addToast }) => {
  const {
    loading, inactiveAccounts,
    fetchAllData, fetchAlerts,
    expandedClient, setExpandedClient,
    activeTab, setActiveTab,
    filterStatus, setFilterStatus,
    timeframe, handleTimeframeChange,
    accountSearch, setAccountSearch,
    aiAnalysisClient, setAiAnalysisClient,
    healthData,
    alerts, alertsUnreadCount, markAlertAsRead, resolveAlert,
    aiInsights, aiInsightsLoading, aiInsightsError, fetchAiInsights,
    platformClients, filteredClients,
  } = usePerformanceData({
    platform: 'META',
    label: 'Meta',
    inactiveKey: 'meta_inactive',
    fallbackRanges: FALLBACK_RANGES,
    scoreClient: calculateClientScore,
    addToast,
  });

  // CSV download state
  const [csvDownloading, setCsvDownloading] = useState(false);

  // Download accounts as CSV
  const handleDownloadCSV = async () => {
    setCsvDownloading(true);
    try {
      const res = await fetch(`${API_URL}/export/accounts-csv?platform=meta&date_range=${timeframe}`);
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `signal-meta-${timeframe}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch {
      toast.error('CSV export failed');
    }
    setCsvDownloading(false);
  };

  const summaryStats = useMemo(() => {
    const totalSpend = platformClients.reduce((sum, c) => sum + (c.metrics?.spend || 0), 0);
    const totalLeads = platformClients.reduce((sum, c) => sum + (c.metrics?.leads || 0), 0);
    // Only count messaging_conversations from OUTCOME_ENGAGEMENT/MESSAGES campaigns
    const totalMessages = platformClients.reduce((sum, c) => {
      const campaigns = c.campaigns || [];
      return sum + campaigns.reduce((s, camp) => {
        const isMessagingCampaign = ['OUTCOME_ENGAGEMENT', 'MESSAGES', 'POST_ENGAGEMENT'].includes(camp.objective);
        return s + (isMessagingCampaign ? (camp.messaging_conversations || 0) : 0);
      }, 0);
    }, 0);
    // Also calculate purchases from sales campaigns
    const totalPurchases = platformClients.reduce((sum, c) => {
      const campaigns = c.campaigns || [];
      return sum + campaigns.reduce((s, camp) => {
        const isSalesCampaign = ['OUTCOME_SALES', 'CONVERSIONS', 'PRODUCT_CATALOG_SALES'].includes(camp.objective);
        return s + (isSalesCampaign ? (camp.purchases || 0) : 0);
      }, 0);
    }, 0);
    const totalResults = totalLeads + totalMessages + totalPurchases;
    const avgCPR = totalResults > 0 ? totalSpend / totalResults : 0;
    
    const clientsWithScores = platformClients.map(c => ({
      ...c,
      score: calculateClientScore(c.campaigns || [])
    }));
    
    const goodClients = clientsWithScores.filter(c => c.score.status === 'good').length;
    const warningClients = clientsWithScores.filter(c => c.score.status === 'warning').length;
    const criticalClients = clientsWithScores.filter(c => c.score.status === 'critical').length;
    const needsAttention = warningClients + criticalClients;
    
    // Find top performer (highest score)
    const topPerformer = clientsWithScores.length > 0 
      ? clientsWithScores.reduce((best, c) => c.score.score > best.score.score ? c : best)
      : null;
    
    // Find worst performer (lowest score with spend > 0)
    const activeClients = clientsWithScores.filter(c => c.metrics?.spend > 0);
    const worstPerformer = activeClients.length > 0
      ? activeClients.reduce((worst, c) => c.score.score < worst.score.score ? c : worst)
      : null;

    return { 
      totalSpend, 
      totalLeads, 
      totalMessages, 
      totalResults, 
      avgCPR,
      goodClients, 
      warningClients, 
      criticalClients,
      needsAttention,
      topPerformer,
      worstPerformer
    };
  }, [platformClients]);

  // Helper function for objective names - MUST be defined before insightsData useMemo
  const getObjectiveShortName = (obj) => {
    const mapping = {
      'OUTCOME_LEADS': 'Leads',
      'LEAD_GENERATION': 'Leads',
      'OUTCOME_ENGAGEMENT': 'WhatsApp',
      'MESSAGES': 'Messages',
      'OUTCOME_TRAFFIC': 'Traffic',
      'LINK_CLICKS': 'Traffic',
      'OUTCOME_SALES': 'Sales',
      'OUTCOME_AWARENESS': 'Awareness',
    };
    return mapping[obj] || obj?.replace('OUTCOME_', '') || 'Other';
  };

  // State for collapsible insight categories
  const [expandedCategories, setExpandedCategories] = useState({
    aiInsights: true,  // AI Insights expanded by default
    immediate: true,
    trends: true,
    conversion: false,
    budget: false,
    efficiency: false
  });

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  // Advanced insights data for the goldmine section - NOW WITH 18 TOTAL LISTS
  const insightsData = useMemo(() => {
    // Exclude non-conversion objectives: Traffic, Awareness, Reach, Profile Visits
    const excludedObjectives = [
      'OUTCOME_TRAFFIC', 'LINK_CLICKS', 'WEBSITE_TRAFFIC',
      'OUTCOME_AWARENESS', 'BRAND_AWARENESS', 'REACH', 'VIDEO_VIEWS',
      'PROFILE_VISIT', 'PROFILE_VISITS', 'OUTCOME_PROFILE_VISIT'
    ];
    
    const allCampaigns = platformClients
      .flatMap(c => c.campaigns || [])
      .filter(camp => camp.spend > 0)
      .filter(camp => !excludedObjectives.includes(camp.objective));
    
    // Helper to get campaign with client info
    const enrichCampaign = (camp) => {
      const client = platformClients.find(c => c.campaigns?.some(cc => cc.id === camp.id));
      const isLead = ['OUTCOME_LEADS', 'LEAD_GENERATION'].includes(camp.objective);
      const isMessage = ['OUTCOME_ENGAGEMENT', 'MESSAGES'].includes(camp.objective);
      const isSales = ['OUTCOME_SALES', 'CONVERSIONS'].includes(camp.objective);
      const results = isLead ? (camp.leads || 0) : isMessage ? (camp.messaging_conversations || 0) : isSales ? (camp.purchases || 0) : (camp.clicks || 0);
      const cpr = results > 0 ? camp.spend / results : Infinity;
      const frequency = camp.reach > 0 ? (camp.impressions / camp.reach) : 0;
      const cpc = camp.clicks > 0 ? camp.spend / camp.clicks : 0;
      const cpm = camp.impressions > 0 ? (camp.spend / camp.impressions) * 1000 : 0;
      return { 
        ...camp, 
        cpr, 
        results, 
        frequency, 
        cpc, 
        cpm,
        clientName: client?.name,
        clientId: client?.id,
        isLead,
        isMessage,
        isSales,
        type: isLead ? 'Lead' : isMessage ? 'Message' : isSales ? 'Sales' : 'Traffic'
      };
    };

    const enrichedCampaigns = allCampaigns.map(enrichCampaign);
    
    // Calculate averages for comparison
    const avgCPR = enrichedCampaigns.filter(c => c.cpr < Infinity && c.results > 0).reduce((sum, c) => sum + c.cpr, 0) / 
                   (enrichedCampaigns.filter(c => c.cpr < Infinity && c.results > 0).length || 1);
    const avgCPC = enrichedCampaigns.filter(c => c.cpc > 0).reduce((sum, c) => sum + c.cpc, 0) / 
                   (enrichedCampaigns.filter(c => c.cpc > 0).length || 1);
    const avgCPM = enrichedCampaigns.filter(c => c.cpm > 0).reduce((sum, c) => sum + c.cpm, 0) / 
                   (enrichedCampaigns.filter(c => c.cpm > 0).length || 1);
    const avgCTR = enrichedCampaigns.filter(c => c.ctr > 0).reduce((sum, c) => sum + c.ctr, 0) / 
                   (enrichedCampaigns.filter(c => c.ctr > 0).length || 1);

    // ==================== ORIGINAL 7 LISTS ====================
    
    // 1. SCALE OPPORTUNITIES - Low CPR, low spend, ready to scale
    const scaleOpportunities = enrichedCampaigns
      .filter(camp => camp.cpr < 150 && camp.spend < 2000 && camp.results >= 3)
      .sort((a, b) => a.cpr - b.cpr)
      .slice(0, 5);

    // 2. BUDGET DRAINS - High spend but poor results
    const budgetDrains = enrichedCampaigns
      .filter(camp => (camp.cpr > 300 || camp.results === 0) && camp.spend >= 500)
      .sort((a, b) => b.spend - a.spend)
      .slice(0, 5);

    // 3. CREATIVE FATIGUE - High frequency campaigns (2.0+ is where performance drops meaningfully)
    const creativeFatigue = enrichedCampaigns
      .filter(camp => camp.frequency > 2.0)
      .sort((a, b) => b.frequency - a.frequency)
      .slice(0, 8);

    // 4. TOP PERFORMERS (Lowest CPR)
    const topEfficient = enrichedCampaigns
      .filter(camp => camp.results >= 3 && camp.cpr < Infinity && (camp.isLead || camp.isMessage))
      .sort((a, b) => a.cpr - b.cpr)
      .slice(0, 8);

    // 5. QUICK WINS (Low CTR - Needs Refresh)
    const quickWins = enrichedCampaigns
      .filter(camp => camp.ctr < 1 && camp.spend > 500)
      .sort((a, b) => b.spend - a.spend)
      .slice(0, 5);

    // 6. SPEND BY CLIENT - FIXED: Only count messaging from messaging campaigns
    const spendByClient = platformClients
      .filter(c => c.metrics?.spend > 0)
      .map(c => {
        // Count messages only from messaging campaigns
        const messagingResults = (c.campaigns || []).reduce((s, camp) => {
          const isMessagingCampaign = ['OUTCOME_ENGAGEMENT', 'MESSAGES', 'POST_ENGAGEMENT'].includes(camp.objective);
          return s + (isMessagingCampaign ? (camp.messaging_conversations || 0) : 0);
        }, 0);
        // Count purchases from sales campaigns
        const salesResults = (c.campaigns || []).reduce((s, camp) => {
          const isSalesCampaign = ['OUTCOME_SALES', 'CONVERSIONS', 'PRODUCT_CATALOG_SALES'].includes(camp.objective);
          return s + (isSalesCampaign ? (camp.purchases || 0) : 0);
        }, 0);
        return {
          name: c.name?.length > 12 ? c.name.substring(0, 12) + '...' : c.name,
          fullName: c.name,
          id: c.id,
          spend: c.metrics?.spend || 0,
          results: (c.metrics?.leads || 0) + messagingResults + salesResults,
          score: calculateClientScore(c.campaigns || []).score
        };
      })
      .sort((a, b) => b.spend - a.spend)
      .slice(0, 10);

    // 7. OBJECTIVE PERFORMANCE
    const objectivePerf = {};
    enrichedCampaigns.forEach(camp => {
      const obj = camp.objective || 'OTHER';
      if (!objectivePerf[obj]) objectivePerf[obj] = { spend: 0, results: 0, campaigns: 0 };
      objectivePerf[obj].spend += camp.spend || 0;
      objectivePerf[obj].results += camp.results;
      objectivePerf[obj].campaigns += 1;
    });
    const objectiveData = Object.entries(objectivePerf)
      .map(([obj, data]) => ({
        objective: getObjectiveShortName(obj),
        fullName: obj,
        spend: data.spend,
        results: data.results,
        campaigns: data.campaigns,
        cpr: data.results > 0 ? data.spend / data.results : 0,
      }))
      .filter(d => d.spend > 0)
      .sort((a, b) => b.spend - a.spend);

    // ==================== NEW 11 LISTS ====================

    // 8. DECLINING PERFORMERS - CPR increased significantly (simulated with high CPR + low results ratio)
    const decliningPerformers = enrichedCampaigns
      .filter(camp => camp.cpr > avgCPR * 1.3 && camp.results > 0 && camp.spend > 300)
      .map(camp => ({
        ...camp,
        cprIncrease: ((camp.cpr - avgCPR) / avgCPR * 100).toFixed(0),
        vsAvg: camp.cpr / avgCPR
      }))
      .sort((a, b) => b.vsAvg - a.vsAvg)
      .slice(0, 5);

    // 9. RISING STARS - Good results with improving efficiency (low CPR, decent spend)
    const risingStars = enrichedCampaigns
      .filter(camp => camp.cpr < avgCPR * 0.7 && camp.results >= 5 && camp.spend >= 300)
      .map(camp => ({
        ...camp,
        efficiency: ((avgCPR - camp.cpr) / avgCPR * 100).toFixed(0),
        belowAvg: avgCPR / camp.cpr
      }))
      .sort((a, b) => b.belowAvg - a.belowAvg)
      .slice(0, 5);

    // 10. COST SPIKE ALERT - CPR way above average
    const costSpikeAlerts = enrichedCampaigns
      .filter(camp => camp.cpr > avgCPR * 1.5 && camp.results > 0 && camp.spend > 500)
      .map(camp => ({
        ...camp,
        spikePercent: ((camp.cpr - avgCPR) / avgCPR * 100).toFixed(0),
        severity: camp.cpr > avgCPR * 2 ? 'critical' : 'warning'
      }))
      .sort((a, b) => b.cpr - a.cpr)
      .slice(0, 5);

    // 11. CLICK-TO-LEAD GAP - High clicks but low conversions (landing page issue)
    const clickToLeadGap = enrichedCampaigns
      .filter(camp => {
        if (!camp.isLead && !camp.isMessage) return false;
        const convRate = camp.clicks > 0 ? (camp.results / camp.clicks) * 100 : 0;
        return camp.clicks > 50 && convRate < 2 && camp.spend > 300;
      })
      .map(camp => ({
        ...camp,
        convRate: camp.clicks > 0 ? ((camp.results / camp.clicks) * 100).toFixed(2) : 0,
        potentialLeads: Math.round(camp.clicks * 0.05) // 5% benchmark
      }))
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, 5);

    // 12. SLOW CONVERTERS - High impressions, low CTR, but decent results
    const slowConverters = enrichedCampaigns
      .filter(camp => camp.ctr < avgCTR * 0.7 && camp.results >= 3 && camp.impressions > 10000)
      .map(camp => ({
        ...camp,
        ctrGap: (avgCTR - camp.ctr).toFixed(2),
        potentialClicks: Math.round(camp.impressions * avgCTR / 100)
      }))
      .sort((a, b) => b.impressions - a.impressions)
      .slice(0, 5);

    // 13. HIDDEN GEMS - Excellent efficiency, minimal spend (underexploited)
    const hiddenGems = enrichedCampaigns
      .filter(camp => camp.cpr < avgCPR * 0.5 && camp.spend < 1000 && camp.results >= 5)
      .map(camp => ({
        ...camp,
        efficiencyRating: (avgCPR / camp.cpr).toFixed(1),
        scalePotential: camp.spend < 500 ? 'High' : 'Medium'
      }))
      .sort((a, b) => a.cpr - b.cpr)
      .slice(0, 5);

    // 14. BUDGET MISALLOCATED - High CPR campaigns getting more budget than efficient ones
    const efficientCampaigns = enrichedCampaigns.filter(c => c.cpr < avgCPR && c.results > 0).sort((a, b) => a.cpr - b.cpr);
    const inefficientHighSpend = enrichedCampaigns.filter(c => c.cpr > avgCPR * 1.2 && c.spend > 500);
    const budgetMisallocated = inefficientHighSpend
      .map(camp => {
        const betterOption = efficientCampaigns.find(e => e.clientId !== camp.clientId) || efficientCampaigns[0];
        return {
          ...camp,
          betterAlternative: betterOption?.name,
          betterCPR: betterOption?.cpr,
          wastedAmount: camp.results > 0 ? Math.round(camp.spend - (camp.results * avgCPR)) : camp.spend
        };
      })
      .filter(c => c.wastedAmount > 100)
      .sort((a, b) => b.wastedAmount - a.wastedAmount)
      .slice(0, 5);

    // 15. ROAS LEADERS - For sales campaigns
    const roasLeaders = enrichedCampaigns
      .filter(camp => camp.isSales && camp.roas > 0)
      .map(camp => ({
        ...camp,
        roasValue: camp.roas || 0,
        revenue: (camp.roas || 0) * camp.spend
      }))
      .sort((a, b) => b.roasValue - a.roasValue)
      .slice(0, 5);

    // 16. MESSAGE VOLUME OPPORTUNITY - WhatsApp campaigns with low CPMC
    const messageOpportunity = enrichedCampaigns
      .filter(camp => camp.isMessage && camp.cpr < 100 && camp.results >= 3)
      .map(camp => ({
        ...camp,
        cpmc: camp.cpr,
        scaleRoom: camp.spend < 1000 ? 'High' : camp.spend < 3000 ? 'Medium' : 'Low'
      }))
      .sort((a, b) => a.cpmc - b.cpmc)
      .slice(0, 5);

    // 17. CPM WATCH - Unusually high CPMs (audience saturation)
    const cpmWatch = enrichedCampaigns
      .filter(camp => camp.cpm > avgCPM * 1.5 && camp.impressions > 5000)
      .map(camp => ({
        ...camp,
        cpmIncrease: ((camp.cpm - avgCPM) / avgCPM * 100).toFixed(0),
        severity: camp.cpm > avgCPM * 2 ? 'critical' : 'warning'
      }))
      .sort((a, b) => b.cpm - a.cpm)
      .slice(0, 5);

    // 18. CPC OUTLIERS - Abnormal cost-per-click
    const cpcOutliers = enrichedCampaigns
      .filter(camp => camp.cpc > avgCPC * 1.5 && camp.clicks > 20)
      .map(camp => ({
        ...camp,
        cpcIncrease: ((camp.cpc - avgCPC) / avgCPC * 100).toFixed(0),
        potentialSavings: Math.round((camp.cpc - avgCPC) * camp.clicks)
      }))
      .sort((a, b) => b.cpc - a.cpc)
      .slice(0, 5);

    return {
      // Original 7
      scaleOpportunities,
      budgetDrains,
      creativeFatigue,
      topEfficient,
      quickWins,
      spendByClient,
      objectiveData,
      // New 11
      decliningPerformers,
      risingStars,
      costSpikeAlerts,
      clickToLeadGap,
      slowConverters,
      hiddenGems,
      budgetMisallocated,
      roasLeaders,
      messageOpportunity,
      cpmWatch,
      cpcOutliers,
      // Metadata
      totalCampaigns: allCampaigns.length,
      avgCPR,
      avgCPC,
      avgCPM,
      avgCTR
    };
  }, [platformClients]);


  if (loading && platformClients.length === 0) {
    return (
      <div className="space-y-4 sm:space-y-6" data-testid="meta-performance-loading">
        <div className="h-24 sm:h-32 bg-zinc-800 rounded-xl sm:rounded-2xl animate-pulse" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-20 sm:h-24 bg-zinc-800 rounded-xl animate-pulse" />)}
        </div>
        {[...Array(5)].map((_, i) => <div key={i} className="animate-pulse bg-zinc-800 rounded-lg h-16 mb-2" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="meta-performance-page">
      {/* Header with Timeframe Selector */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="w-10 h-10 bg-[#1877F2] rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </div>
            Meta Ads Performance
          </h1>
          <p className="text-zinc-500 text-sm mt-1">
            Sorted by spend (highest first) • {platformClients.length} active accounts
          </p>
        </div>
        
        {/* Timeframe Selector + Actions */}
        <div className="flex items-center gap-2">
          <select
            value={timeframe}
            onChange={(e) => handleTimeframeChange(e.target.value)}
            className="bg-[#18181B] text-white border border-white/10 rounded-xl px-4 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#1877F2]/50"
            data-testid="timeframe-selector"
          >
            {TIMEFRAME_OPTIONS.map(opt => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>

          {/* Download CSV */}
          <button
            onClick={handleDownloadCSV}
            disabled={csvDownloading}
            className="flex items-center gap-2 px-3 py-2 bg-[#18181B] border border-white/10 rounded-lg text-xs text-zinc-400 hover:text-white hover:border-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Download accounts as CSV"
          >
            <Download className={`w-4 h-4 ${csvDownloading ? 'animate-pulse' : ''}`} />
            <span className="hidden sm:inline">{csvDownloading ? 'Exporting...' : 'CSV'}</span>
          </button>

          <button
            onClick={() => fetchAllData()}
            className="px-4 py-2.5 bg-gradient-to-r from-[#1877F2] to-[#6366f1] text-white rounded-xl font-semibold text-sm hover:opacity-90 transition-colors shadow-lg shadow-[#1877F2]/20 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-[#18181B] p-1 rounded-xl border border-white/10 w-fit overflow-x-auto">
        <button
          onClick={() => setActiveTab('performance')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'performance'
              ? 'bg-gradient-to-r from-[#1877F2] to-[#6366f1] text-white'
              : 'text-zinc-400 hover:text-white'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <span>Performance</span>
          <span className={`px-1.5 py-0.5 rounded text-[10px] ${activeTab === 'performance' ? 'bg-white/20' : 'bg-white/10'}`}>
            {platformClients.length}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('health')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'health'
              ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white'
              : 'text-zinc-400 hover:text-white'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <span>Health</span>
          {healthData && (healthData.warning?.length > 0 || healthData.critical?.length > 0) && (
            <span className={`px-1.5 py-0.5 rounded text-[10px] ${activeTab === 'health' ? 'bg-white/20' : 'bg-amber-500/50 text-white'}`}>
              {(healthData.warning?.length || 0) + (healthData.critical?.length || 0)}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('alerts')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'alerts'
              ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
              : 'text-zinc-400 hover:text-white'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          <span>Alerts</span>
          {alertsUnreadCount > 0 && (
            <span className={`px-1.5 py-0.5 rounded text-[10px] animate-pulse ${activeTab === 'alerts' ? 'bg-white/20' : 'bg-red-500 text-white'}`}>
              {alertsUnreadCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('inactive')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'inactive'
              ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white'
              : 'text-zinc-400 hover:text-white'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
          <span>Inactive</span>
          {inactiveAccounts.length > 0 && (
            <span className={`px-1.5 py-0.5 rounded text-[10px] ${activeTab === 'inactive' ? 'bg-white/20' : 'bg-red-500/50 text-white'}`}>
              {inactiveAccounts.length}
            </span>
          )}
        </button>
      </div>

      {/* Performance Tab */}
      {activeTab === 'performance' && (
        <>
          {/* Summary Banner - Action-focused metrics */}
          <div className="bg-gradient-to-r from-[#1877F2]/10 via-[#0D0D0F] to-transparent rounded-2xl border border-[#1877F2]/20 p-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
              {/* Total Spend */}
              <div className="bg-white/5 rounded-xl p-4 border border-white/5">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Total Spend</p>
                <p className="text-3xl font-bold text-white font-mono">{formatCurrency(summaryStats.totalSpend)}</p>
                <p className="text-xs text-zinc-600 mt-1">{platformClients.length} active accounts</p>
              </div>
              
              {/* Results with CPR */}
              <div className="bg-white/5 rounded-xl p-4 border border-white/5">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Results</p>
                <p className="text-3xl font-bold text-emerald-400 font-mono">{formatNumber(summaryStats.totalResults)}</p>
                <p className="text-xs text-emerald-500/80 mt-1">
                  Avg CPR: {formatCurrency(summaryStats.avgCPR)}
                </p>
              </div>
              
              {/* Needs Attention - Critical Action */}
              <div 
                className={`rounded-xl p-4 border cursor-pointer transition-colors ${
                  summaryStats.needsAttention > 0 
                    ? 'bg-red-500/10 border-red-500/30 hover:border-red-500/50' 
                    : 'bg-emerald-500/10 border-emerald-500/30'
                }`}
                onClick={() => summaryStats.needsAttention > 0 && setFilterStatus('critical')}
              >
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  {summaryStats.needsAttention > 0 && (
                    <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse"></span>
                  )}
                  Needs Action
                </p>
                <p className={`text-3xl font-bold font-mono ${summaryStats.needsAttention > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                  {summaryStats.needsAttention}
                </p>
                <p className="text-xs text-zinc-600 mt-1">
                  {summaryStats.criticalClients} critical • {summaryStats.warningClients} warning
                </p>
              </div>
              
              {/* Top Performer */}
              {summaryStats.topPerformer && (
                <div 
                  className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/30 cursor-pointer hover:border-emerald-500/50 transition-colors"
                  onClick={() => setExpandedClient(summaryStats.topPerformer.id)}
                >
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                    <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg> Top Performer
                  </p>
                  <p className="text-lg font-bold text-white truncate" title={summaryStats.topPerformer.name}>
                    {summaryStats.topPerformer.name?.length > 15 
                      ? summaryStats.topPerformer.name.substring(0, 15) + '...' 
                      : summaryStats.topPerformer.name}
                  </p>
                  <p className="text-xs text-emerald-400 mt-1">
                    Score: {summaryStats.topPerformer.score.score} • {formatCurrency(summaryStats.topPerformer.metrics?.spend)} spend
                  </p>
                </div>
              )}
              
              {/* Worst Performer - Action needed */}
              {summaryStats.worstPerformer && summaryStats.worstPerformer.score.score < 60 && (
                <div 
                  className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/30 cursor-pointer hover:border-amber-500/50 transition-colors"
                  onClick={() => setExpandedClient(summaryStats.worstPerformer.id)}
                >
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                    <svg className="w-4 h-4 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg> Needs Review
                  </p>
                  <p className="text-lg font-bold text-white truncate" title={summaryStats.worstPerformer.name}>
                    {summaryStats.worstPerformer.name?.length > 15 
                      ? summaryStats.worstPerformer.name.substring(0, 15) + '...' 
                      : summaryStats.worstPerformer.name}
                  </p>
                  <p className="text-xs text-amber-400 mt-1">
                    Score: {summaryStats.worstPerformer.score.score} • {formatCurrency(summaryStats.worstPerformer.metrics?.spend)} spend
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* === PERFORMANCE INSIGHTS - THE ULTIMATE EDGE === */}
          <div className="space-y-3">
            {/* Main Header with Stats */}
            <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-transparent rounded-2xl border border-amber-500/20 p-5">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
                    <span className="text-2xl">💎</span>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Performance Insights</h2>
                    <p className="text-sm text-zinc-400">Actionable data to find winning advantages</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-white/5 rounded-lg px-3 py-2 text-center">
                    <p className="text-lg font-bold text-white">{insightsData.totalCampaigns}</p>
                    <p className="text-[10px] text-zinc-500 uppercase">Campaigns</p>
                  </div>
                  <div className="bg-white/5 rounded-lg px-3 py-2 text-center">
                    <p className="text-lg font-bold text-emerald-400">{formatCurrency(insightsData.avgCPR)}</p>
                    <p className="text-[10px] text-zinc-500 uppercase">Avg CPR</p>
                  </div>
                  <div className="bg-white/5 rounded-lg px-3 py-2 text-center">
                    <p className="text-lg font-bold text-amber-400">{insightsData.avgCTR?.toFixed(2)}%</p>
                    <p className="text-[10px] text-zinc-500 uppercase">Avg CTR</p>
                  </div>
                  {/* AI Insights Button */}
                  <button
                    onClick={() => fetchAiInsights(timeframe)}
                    disabled={aiInsightsLoading}
                    className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-colors disabled:opacity-50"
                  >
                    {aiInsightsLoading ? (
                      <>
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4" />
                        AI Analysis
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* ==================== AI POWERED INSIGHTS ==================== */}
            {aiInsights && (
              <div className="bg-[#09090B] rounded-2xl border border-violet-500/30 overflow-hidden">
                <button
                  className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                  onClick={() => toggleCategory('aiInsights')}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                      <Zap className="w-5 h-5 text-white" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-sm font-bold text-violet-400">AI-POWERED INSIGHTS</h3>
                      <p className="text-xs text-zinc-500">GPT-4o analysis of your portfolio</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {aiInsights.smart_scores && (
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        aiInsights.smart_scores.overall_health >= 70 ? 'bg-emerald-500/20 text-emerald-400' :
                        aiInsights.smart_scores.overall_health >= 40 ? 'bg-amber-500/20 text-amber-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        Health: {aiInsights.smart_scores.overall_health}/100
                      </span>
                    )}
                    <svg className={`w-5 h-5 text-zinc-400 transition-transform ${expandedCategories.aiInsights ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>
                {expandedCategories.aiInsights && (
                  <div className="p-4 pt-0 space-y-4">
                    {/* AI Summary */}
                    {aiInsights.summary && (
                      <div className="bg-violet-500/10 rounded-xl p-4 border border-violet-500/20">
                        <h4 className="text-xs font-bold text-violet-400 uppercase mb-2 flex items-center gap-1.5">
                          <svg className="w-4 h-4 text-[#6366f1]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg> Executive Summary
                        </h4>
                        <p className="text-sm text-zinc-300 leading-relaxed">{aiInsights.summary}</p>
                      </div>
                    )}
                    
                    {/* Smart Scores */}
                    {aiInsights.smart_scores && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-white/5 rounded-lg p-3 text-center">
                          <p className={`text-2xl font-bold ${
                            aiInsights.smart_scores.overall_health >= 70 ? 'text-emerald-400' :
                            aiInsights.smart_scores.overall_health >= 40 ? 'text-amber-400' : 'text-red-400'
                          }`}>{aiInsights.smart_scores.overall_health}</p>
                          <p className="text-[10px] text-zinc-500 uppercase">Overall Health</p>
                        </div>
                        <div className="bg-white/5 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-blue-400">{aiInsights.smart_scores.efficiency_score}</p>
                          <p className="text-[10px] text-zinc-500 uppercase">Efficiency</p>
                        </div>
                        <div className="bg-white/5 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-cyan-400">{aiInsights.smart_scores.growth_potential}</p>
                          <p className="text-[10px] text-zinc-500 uppercase">Growth Potential</p>
                        </div>
                        <div className="bg-white/5 rounded-lg p-3 text-center">
                          <p className={`text-lg font-bold ${
                            aiInsights.smart_scores.risk_level === 'low' ? 'text-emerald-400' :
                            aiInsights.smart_scores.risk_level === 'medium' ? 'text-amber-400' : 'text-red-400'
                          }`}>{aiInsights.smart_scores.risk_level?.toUpperCase()}</p>
                          <p className="text-[10px] text-zinc-500 uppercase">Risk Level</p>
                        </div>
                      </div>
                    )}

                    {/* AI Insights List */}
                    {aiInsights.ai_insights && aiInsights.ai_insights.length > 0 && (
                      <div className="bg-white/5 rounded-xl p-4">
                        <h4 className="text-xs font-bold text-violet-400 uppercase mb-3 flex items-center gap-1.5">
                          <svg className="w-4 h-4 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg> Key Insights
                        </h4>
                        <div className="space-y-3">
                          {aiInsights.ai_insights.map((insight, i) => (
                            <div key={i} className={`rounded-lg p-3 border ${
                              insight.impact === 'high' ? 'bg-red-500/10 border-red-500/20' :
                              insight.impact === 'medium' ? 'bg-amber-500/10 border-amber-500/20' :
                              'bg-blue-500/10 border-blue-500/20'
                            }`}>
                              <div className="flex items-start justify-between gap-2">
                                <h5 className="text-sm font-bold text-white">{insight.title}</h5>
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                                  insight.impact === 'high' ? 'bg-red-500/20 text-red-400' :
                                  insight.impact === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                                  'bg-blue-500/20 text-blue-400'
                                }`}>{insight.impact}</span>
                              </div>
                              <p className="text-xs text-zinc-400 mt-1">{insight.description}</p>
                              {insight.action && (
                                <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1">
                                  <span>→</span> {insight.action}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* AI Alerts */}
                    {aiInsights.ai_alerts && aiInsights.ai_alerts.length > 0 && (
                      <div className="bg-red-500/5 rounded-xl p-4 border border-red-500/20">
                        <h4 className="text-xs font-bold text-red-400 uppercase mb-3 flex items-center gap-1.5">
                          <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg> Critical Alerts
                        </h4>
                        <div className="space-y-2">
                          {aiInsights.ai_alerts.map((alert, i) => (
                            <div key={i} className={`rounded-lg p-3 ${
                              alert.severity === 'critical' ? 'bg-red-500/10' : 'bg-amber-500/10'
                            }`}>
                              <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${
                                  alert.severity === 'critical' ? 'bg-red-400 animate-pulse' : 'bg-amber-400'
                                }`}></span>
                                <h5 className="text-sm font-bold text-white">{alert.title}</h5>
                              </div>
                              <p className="text-xs text-zinc-400 mt-1">{alert.message}</p>
                              {alert.action_required && (
                                <p className="text-xs text-cyan-400 mt-2">Action: {alert.action_required}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Top Recommendations */}
                    {aiInsights.recommendations && aiInsights.recommendations.length > 0 && (
                      <div className="bg-emerald-500/5 rounded-xl p-4 border border-emerald-500/20">
                        <h4 className="text-xs font-bold text-emerald-400 uppercase mb-3 flex items-center gap-1.5">
                          <svg className="w-4 h-4 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> Top Recommendations
                        </h4>
                        <div className="space-y-2">
                          {aiInsights.recommendations.map((rec, i) => (
                            <div key={i} className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
                              <span className="w-6 h-6 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center text-xs font-bold">
                                {rec.priority}
                              </span>
                              <div>
                                <p className="text-sm text-white">{rec.action}</p>
                                <p className="text-xs text-zinc-500 mt-1">Impact: {rec.expected_impact}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* AI Insights Error State */}
            {aiInsightsError && (
              <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/20">
                <p className="text-sm text-red-400">AI Analysis Error: {aiInsightsError}</p>
                <button 
                  onClick={() => fetchAiInsights(timeframe)}
                  className="mt-2 text-xs text-white bg-red-500/20 hover:bg-red-500/30 px-3 py-1.5 rounded"
                >
                  Retry
                </button>
              </div>
            )}

            {/* ==================== CATEGORY 1: IMMEDIATE ACTION ==================== */}
            <div className="bg-[#09090B] rounded-2xl border border-red-500/20 overflow-hidden">
              <button
                onClick={() => toggleCategory('immediate')}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-500/20 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                  </div>
                  <div className="text-left">
                    <h3 className="text-base font-bold text-red-400">Immediate Action Required</h3>
                    <p className="text-xs text-zinc-500">Issues that need urgent attention</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex gap-2">
                    {insightsData.budgetDrains.length > 0 && (
                      <span className="px-2.5 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-semibold">
                        {insightsData.budgetDrains.length} drains
                      </span>
                    )}
                    {insightsData.budgetDrains.filter(c => c.results === 0).length > 0 && (
                      <span className="px-2.5 py-1 bg-red-500/30 text-red-300 rounded-full text-xs font-semibold animate-pulse">
                        {insightsData.budgetDrains.filter(c => c.results === 0).length} zero results
                      </span>
                    )}
                    {insightsData.costSpikeAlerts.length > 0 && (
                      <span className="px-2.5 py-1 bg-orange-500/20 text-orange-400 rounded-full text-xs font-semibold">
                        {insightsData.costSpikeAlerts.length} cost spikes
                      </span>
                    )}
                  </div>
                  <svg className={`w-5 h-5 text-zinc-400 transition-transform ${expandedCategories.immediate ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              
              {expandedCategories.immediate && (
                <div className="px-5 pb-5 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Budget Drains */}
                    <div className="bg-white/5 rounded-xl border border-red-500/20 overflow-hidden">
                      <div className="bg-red-500/10 px-4 py-3 border-b border-red-500/10">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-bold text-red-400 flex items-center gap-2">
                            <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" /></svg> Budget Drains
                          </h4>
                          <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full font-semibold">
                            {insightsData.budgetDrains.length}
                          </span>
                        </div>
                        <p className="text-[10px] text-zinc-500 mt-1">High spend, poor results</p>
                      </div>
                      <div className="p-3 space-y-2 max-h-52 overflow-y-auto custom-scrollbar">
                        {insightsData.budgetDrains.length > 0 ? insightsData.budgetDrains.map((camp, i) => (
                          <div key={i} className="bg-white/5 rounded-lg p-2.5 hover:bg-white/10 transition-colors cursor-pointer">
                            <p className="text-xs font-medium text-white truncate" title={camp.name}>{camp.name}</p>
                            <p className="text-[10px] text-zinc-500 truncate">{camp.clientName}</p>
                            <div className="flex items-center justify-between mt-1.5">
                              <span className="text-[10px] text-red-400 font-bold">
                                {camp.results === 0 ? '0 results!' : `CPR: ${formatCurrency(camp.cpr)}`}
                              </span>
                              <span className="text-[10px] text-zinc-400">{formatCurrency(camp.spend)} spent</span>
                            </div>
                          </div>
                        )) : (
                          <p className="text-xs text-emerald-400 text-center py-4">✓ No budget drains</p>
                        )}
                      </div>
                    </div>

                    {/* Zero Results Alert */}
                    <div className="bg-white/5 rounded-xl border border-red-500/30 overflow-hidden">
                      <div className="bg-red-500/15 px-4 py-3 border-b border-red-500/20">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-bold text-red-400 flex items-center gap-2">
                            <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg> Zero Results
                          </h4>
                          <span className="text-xs bg-red-500/30 text-red-300 px-2 py-0.5 rounded-full font-semibold animate-pulse">
                            {insightsData.budgetDrains.filter(c => c.results === 0).length}
                          </span>
                        </div>
                        <p className="text-[10px] text-zinc-500 mt-1">Spending with zero conversions - URGENT</p>
                      </div>
                      <div className="p-3 space-y-2 max-h-52 overflow-y-auto custom-scrollbar">
                        {insightsData.budgetDrains.filter(c => c.results === 0).length > 0 ? 
                          insightsData.budgetDrains.filter(c => c.results === 0).map((camp, i) => (
                          <div key={i} className="bg-red-500/5 border border-red-500/20 rounded-lg p-2.5">
                            <p className="text-xs font-medium text-white truncate">{camp.name}</p>
                            <p className="text-[10px] text-zinc-500 truncate">{camp.clientName}</p>
                            <div className="flex items-center justify-between mt-1.5">
                              <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded text-[10px] font-semibold">0 RESULTS</span>
                              <span className="text-[10px] text-red-400 font-bold">{formatCurrency(camp.spend)} wasted</span>
                            </div>
                          </div>
                        )) : (
                          <div className="text-center py-4">
                            <svg className="w-6 h-6 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            <p className="text-xs text-emerald-400 mt-2">All campaigns generating results!</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Cost Spike Alerts - NEW */}
                    <div className="bg-white/5 rounded-xl border border-orange-500/20 overflow-hidden">
                      <div className="bg-orange-500/10 px-4 py-3 border-b border-orange-500/10">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-bold text-orange-400 flex items-center gap-2">
                            <svg className="w-4 h-4 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg> Cost Spike Alert
                          </h4>
                          <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded-full font-semibold">
                            {insightsData.costSpikeAlerts.length}
                          </span>
                        </div>
                        <p className="text-[10px] text-zinc-500 mt-1">CPR increased &gt;50% vs average</p>
                      </div>
                      <div className="p-3 space-y-2 max-h-52 overflow-y-auto custom-scrollbar">
                        {insightsData.costSpikeAlerts.length > 0 ? insightsData.costSpikeAlerts.map((camp, i) => (
                          <div key={i} className={`rounded-lg p-2.5 ${camp.severity === 'critical' ? 'bg-red-500/10 border border-red-500/20' : 'bg-orange-500/10 border border-orange-500/20'}`}>
                            <p className="text-xs font-medium text-white truncate">{camp.name}</p>
                            <p className="text-[10px] text-zinc-500 truncate">{camp.clientName}</p>
                            <div className="flex items-center justify-between mt-1.5">
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${camp.severity === 'critical' ? 'bg-red-500/20 text-red-400' : 'bg-orange-500/20 text-orange-400'}`}>
                                +{camp.spikePercent}% spike
                              </span>
                              <span className="text-[10px] text-zinc-400">CPR: {formatCurrency(camp.cpr)}</span>
                            </div>
                          </div>
                        )) : (
                          <div className="text-center py-4">
                            <svg className="w-6 h-6 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            <p className="text-xs text-emerald-400 mt-2">No cost spikes detected!</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ==================== CATEGORY 2: TRENDS & MOMENTUM ==================== */}
            <div className="bg-[#09090B] rounded-2xl border border-blue-500/20 overflow-hidden">
              <button
                onClick={() => toggleCategory('trends')}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#6366f1]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                  </div>
                  <div className="text-left">
                    <h3 className="text-base font-bold text-blue-400">Trends & Momentum</h3>
                    <p className="text-xs text-zinc-500">Track rising stars and declining performers</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex gap-2">
                    {insightsData.risingStars.length > 0 && (
                      <span className="px-2.5 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-xs font-semibold">
                        {insightsData.risingStars.length} rising
                      </span>
                    )}
                    {insightsData.decliningPerformers.length > 0 && (
                      <span className="px-2.5 py-1 bg-amber-500/20 text-amber-400 rounded-full text-xs font-semibold">
                        {insightsData.decliningPerformers.length} declining
                      </span>
                    )}
                  </div>
                  <svg className={`w-5 h-5 text-zinc-400 transition-transform ${expandedCategories.trends ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              
              {expandedCategories.trends && (
                <div className="px-5 pb-5 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Rising Stars - NEW */}
                    <div className="bg-white/5 rounded-xl border border-emerald-500/20 overflow-hidden">
                      <div className="bg-emerald-500/10 px-4 py-3 border-b border-emerald-500/10">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-bold text-emerald-400 flex items-center gap-2">
                            <span>📈</span> Rising Stars
                          </h4>
                          <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full font-semibold">
                            {insightsData.risingStars.length}
                          </span>
                        </div>
                        <p className="text-[10px] text-zinc-500 mt-1">Outperforming average by 30%+</p>
                      </div>
                      <div className="p-3 space-y-2 max-h-52 overflow-y-auto custom-scrollbar">
                        {insightsData.risingStars.length > 0 ? insightsData.risingStars.map((camp, i) => (
                          <div key={i} className="bg-emerald-500/5 border border-emerald-500/10 rounded-lg p-2.5">
                            <div className="flex items-center justify-between">
                              <p className="text-xs font-medium text-white truncate flex-1" title={camp.name}>{camp.name}</p>
                              <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-[10px] font-bold ml-2">
                                {camp.efficiency}% better
                              </span>
                            </div>
                            <p className="text-[10px] text-zinc-500 truncate">{camp.clientName}</p>
                            <div className="flex items-center justify-between mt-1.5">
                              <span className="text-[10px] text-emerald-400 font-bold">CPR: {formatCurrency(camp.cpr)}</span>
                              <span className="text-[10px] text-zinc-400">{camp.results} results</span>
                            </div>
                          </div>
                        )) : (
                          <p className="text-xs text-zinc-500 text-center py-4">No rising stars yet</p>
                        )}
                      </div>
                    </div>

                    {/* Declining Performers - NEW */}
                    <div className="bg-white/5 rounded-xl border border-amber-500/20 overflow-hidden">
                      <div className="bg-amber-500/10 px-4 py-3 border-b border-amber-500/10">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-bold text-amber-400 flex items-center gap-2">
                            <span>📉</span> Declining Performers
                          </h4>
                          <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full font-semibold">
                            {insightsData.decliningPerformers.length}
                          </span>
                        </div>
                        <p className="text-[10px] text-zinc-500 mt-1">CPR above average - needs review</p>
                      </div>
                      <div className="p-3 space-y-2 max-h-52 overflow-y-auto custom-scrollbar">
                        {insightsData.decliningPerformers.length > 0 ? insightsData.decliningPerformers.map((camp, i) => (
                          <div key={i} className="bg-amber-500/5 border border-amber-500/10 rounded-lg p-2.5">
                            <div className="flex items-center justify-between">
                              <p className="text-xs font-medium text-white truncate flex-1" title={camp.name}>{camp.name}</p>
                              <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-[10px] font-bold ml-2">
                                +{camp.cprIncrease}%
                              </span>
                            </div>
                            <p className="text-[10px] text-zinc-500 truncate">{camp.clientName}</p>
                            <div className="flex items-center justify-between mt-1.5">
                              <span className="text-[10px] text-amber-400 font-bold">CPR: {formatCurrency(camp.cpr)}</span>
                              <span className="text-[10px] text-zinc-400">{formatCurrency(camp.spend)} spent</span>
                            </div>
                          </div>
                        )) : (
                          <div className="text-center py-4">
                            <svg className="w-6 h-6 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            <p className="text-xs text-emerald-400 mt-2">All campaigns on track!</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Creative Fatigue */}
                    <div className="bg-white/5 rounded-xl border border-yellow-500/20 overflow-hidden">
                      <div className="bg-yellow-500/10 px-4 py-3 border-b border-yellow-500/10">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-bold text-yellow-400 flex items-center gap-2">
                            <span>😴</span> Creative Fatigue
                          </h4>
                          <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full font-semibold">
                            {insightsData.creativeFatigue.length}
                          </span>
                        </div>
                        <p className="text-[10px] text-zinc-500 mt-1">High frequency (&gt;2.0x)</p>
                      </div>
                      <div className="p-3 space-y-2 max-h-52 overflow-y-auto custom-scrollbar">
                        {insightsData.creativeFatigue.length > 0 ? insightsData.creativeFatigue.slice(0, 5).map((camp, i) => {
                          const freqStatus = camp.frequency > 3 ? 'critical' : camp.frequency > 2 ? 'warning' : 'watch';
                          return (
                            <div key={i} className={`rounded-lg p-2.5 ${
                              freqStatus === 'critical' ? 'bg-red-500/10 border border-red-500/20' :
                              freqStatus === 'warning' ? 'bg-amber-500/10 border border-amber-500/20' :
                              'bg-white/5'
                            }`}>
                              <div className="flex items-center justify-between">
                                <p className="text-xs font-medium text-white truncate flex-1">{camp.name}</p>
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ml-2 ${
                                  freqStatus === 'critical' ? 'bg-red-500/20 text-red-400' :
                                  freqStatus === 'warning' ? 'bg-amber-500/20 text-amber-400' :
                                  'bg-yellow-500/20 text-yellow-400'
                                }`}>
                                  {camp.frequency.toFixed(2)}x
                                </span>
                              </div>
                              <p className="text-[10px] text-zinc-500">{camp.clientName}</p>
                            </div>
                          );
                        }) : (
                          <div className="text-center py-4">
                            <svg className="w-6 h-6 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            <p className="text-xs text-emerald-400 mt-2">All creatives fresh!</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ==================== CATEGORY 3: CONVERSION & LANDING PAGE ==================== */}
            <div className="bg-[#09090B] rounded-2xl border border-purple-500/20 overflow-hidden">
              <button
                onClick={() => toggleCategory('conversion')}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center">
                    <span className="text-xl">🔍</span>
                  </div>
                  <div className="text-left">
                    <h3 className="text-base font-bold text-purple-400">Conversion & Landing Page Signals</h3>
                    <p className="text-xs text-zinc-500">Diagnose conversion bottlenecks</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex gap-2">
                    {insightsData.clickToLeadGap.length > 0 && (
                      <span className="px-2.5 py-1 bg-purple-500/20 text-purple-400 rounded-full text-xs font-semibold">
                        {insightsData.clickToLeadGap.length} landing page issues
                      </span>
                    )}
                    {insightsData.quickWins.length > 0 && (
                      <span className="px-2.5 py-1 bg-pink-500/20 text-pink-400 rounded-full text-xs font-semibold">
                        {insightsData.quickWins.length} low CTR
                      </span>
                    )}
                  </div>
                  <svg className={`w-5 h-5 text-zinc-400 transition-transform ${expandedCategories.conversion ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              
              {expandedCategories.conversion && (
                <div className="px-5 pb-5 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Click-to-Lead Gap - NEW */}
                    <div className="bg-white/5 rounded-xl border border-purple-500/20 overflow-hidden">
                      <div className="bg-purple-500/10 px-4 py-3 border-b border-purple-500/10">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-bold text-purple-400 flex items-center gap-2">
                            <span>🔍</span> Click-to-Lead Gap
                          </h4>
                          <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full font-semibold">
                            {insightsData.clickToLeadGap.length}
                          </span>
                        </div>
                        <p className="text-[10px] text-zinc-500 mt-1">High clicks, low conversions = LP issue</p>
                      </div>
                      <div className="p-3 space-y-2 max-h-52 overflow-y-auto custom-scrollbar">
                        {insightsData.clickToLeadGap.length > 0 ? insightsData.clickToLeadGap.map((camp, i) => (
                          <div key={i} className="bg-purple-500/5 border border-purple-500/10 rounded-lg p-2.5">
                            <p className="text-xs font-medium text-white truncate">{camp.name}</p>
                            <p className="text-[10px] text-zinc-500 truncate">{camp.clientName}</p>
                            <div className="flex items-center justify-between mt-1.5">
                              <span className="text-[10px] text-purple-400">{formatNumber(camp.clicks)} clicks → {camp.results} leads</span>
                              <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-[10px] font-bold">
                                {camp.convRate}% conv
                              </span>
                            </div>
                            <p className="text-[10px] text-zinc-500 mt-1">Potential: {camp.potentialLeads} leads at 5% benchmark</p>
                          </div>
                        )) : (
                          <div className="text-center py-4">
                            <svg className="w-6 h-6 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            <p className="text-xs text-emerald-400 mt-2">Good conversion rates!</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Slow Converters - NEW */}
                    <div className="bg-white/5 rounded-xl border border-cyan-500/20 overflow-hidden">
                      <div className="bg-cyan-500/10 px-4 py-3 border-b border-cyan-500/10">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-bold text-cyan-400 flex items-center gap-2">
                            <span>⏰</span> Slow Converters
                          </h4>
                          <span className="text-xs bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded-full font-semibold">
                            {insightsData.slowConverters.length}
                          </span>
                        </div>
                        <p className="text-[10px] text-zinc-500 mt-1">Low CTR but getting results - fix creative</p>
                      </div>
                      <div className="p-3 space-y-2 max-h-52 overflow-y-auto custom-scrollbar">
                        {insightsData.slowConverters.length > 0 ? insightsData.slowConverters.map((camp, i) => (
                          <div key={i} className="bg-cyan-500/5 border border-cyan-500/10 rounded-lg p-2.5">
                            <p className="text-xs font-medium text-white truncate">{camp.name}</p>
                            <p className="text-[10px] text-zinc-500 truncate">{camp.clientName}</p>
                            <div className="flex items-center justify-between mt-1.5">
                              <span className="text-[10px] text-cyan-400">CTR: {camp.ctr?.toFixed(2)}% (avg: {insightsData.avgCTR?.toFixed(2)}%)</span>
                              <span className="text-[10px] text-zinc-400">{camp.results} results</span>
                            </div>
                            <p className="text-[10px] text-emerald-400 mt-1">+{camp.potentialClicks} potential clicks</p>
                          </div>
                        )) : (
                          <div className="text-center py-4">
                            <svg className="w-6 h-6 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            <p className="text-xs text-emerald-400 mt-2">CTRs are healthy!</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Low CTR Campaigns */}
                    <div className="bg-white/5 rounded-xl border border-pink-500/20 overflow-hidden">
                      <div className="bg-pink-500/10 px-4 py-3 border-b border-pink-500/10">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-bold text-pink-400 flex items-center gap-2">
                            <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg> Low CTR - Creative Refresh
                          </h4>
                          <span className="text-xs bg-pink-500/20 text-pink-400 px-2 py-0.5 rounded-full font-semibold">
                            {insightsData.quickWins.length}
                          </span>
                        </div>
                        <p className="text-[10px] text-zinc-500 mt-1">High spend, low CTR (&lt;1%)</p>
                      </div>
                      <div className="p-3 space-y-2 max-h-52 overflow-y-auto custom-scrollbar">
                        {insightsData.quickWins.length > 0 ? insightsData.quickWins.map((camp, i) => (
                          <div key={i} className="bg-white/5 rounded-lg p-2.5">
                            <p className="text-xs font-medium text-white truncate">{camp.name}</p>
                            <p className="text-[10px] text-zinc-500 truncate">{camp.clientName}</p>
                            <div className="flex items-center justify-between mt-1.5">
                              <span className="text-[10px] text-pink-400 font-bold">{camp.ctr?.toFixed(2)}% CTR</span>
                              <span className="text-[10px] text-zinc-400">{formatCurrency(camp.spend)} spent</span>
                            </div>
                          </div>
                        )) : (
                          <div className="text-center py-4">
                            <span className="text-2xl">👍</span>
                            <p className="text-xs text-emerald-400 mt-2">All CTRs healthy!</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ==================== CATEGORY 4: BUDGET & SCALE INTELLIGENCE ==================== */}
            <div className="bg-[#09090B] rounded-2xl border border-emerald-500/20 overflow-hidden">
              <button
                onClick={() => toggleCategory('budget')}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                    <span className="text-xl">💰</span>
                  </div>
                  <div className="text-left">
                    <h3 className="text-base font-bold text-emerald-400">Budget & Scale Intelligence</h3>
                    <p className="text-xs text-zinc-500">Find scaling opportunities & budget optimization</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex gap-2">
                    {insightsData.scaleOpportunities.length > 0 && (
                      <span className="px-2.5 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-xs font-semibold">
                        {insightsData.scaleOpportunities.length} scale ready
                      </span>
                    )}
                    {insightsData.hiddenGems.length > 0 && (
                      <span className="px-2.5 py-1 bg-violet-500/20 text-violet-400 rounded-full text-xs font-semibold">
                        {insightsData.hiddenGems.length} hidden gems
                      </span>
                    )}
                    {insightsData.budgetMisallocated.length > 0 && (
                      <span className="px-2.5 py-1 bg-amber-500/20 text-amber-400 rounded-full text-xs font-semibold">
                        {insightsData.budgetMisallocated.length} misallocated
                      </span>
                    )}
                  </div>
                  <svg className={`w-5 h-5 text-zinc-400 transition-transform ${expandedCategories.budget ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              
              {expandedCategories.budget && (
                <div className="px-5 pb-5 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Scale Opportunities */}
                    <div className="bg-white/5 rounded-xl border border-emerald-500/20 overflow-hidden">
                      <div className="bg-emerald-500/10 px-4 py-3 border-b border-emerald-500/10">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-bold text-emerald-400 flex items-center gap-2">
                            <span>🚀</span> Scale Opportunities
                          </h4>
                          <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full font-semibold">
                            {insightsData.scaleOpportunities.length}
                          </span>
                        </div>
                        <p className="text-[10px] text-zinc-500 mt-1">Low CPR, low spend - ready to scale</p>
                      </div>
                      <div className="p-3 space-y-2 max-h-52 overflow-y-auto custom-scrollbar">
                        {insightsData.scaleOpportunities.length > 0 ? insightsData.scaleOpportunities.map((camp, i) => (
                          <div key={i} className="bg-emerald-500/5 border border-emerald-500/10 rounded-lg p-2.5">
                            <p className="text-xs font-medium text-white truncate">{camp.name}</p>
                            <p className="text-[10px] text-zinc-500 truncate">{camp.clientName}</p>
                            <div className="flex items-center justify-between mt-1.5">
                              <span className="text-[10px] text-emerald-400 font-bold">CPR: {formatCurrency(camp.cpr)}</span>
                              <span className="text-[10px] text-zinc-400">{formatCurrency(camp.spend)} • {camp.results} results</span>
                            </div>
                          </div>
                        )) : (
                          <p className="text-xs text-zinc-500 text-center py-4">No scale opportunities found</p>
                        )}
                      </div>
                    </div>

                    {/* Hidden Gems - NEW */}
                    <div className="bg-white/5 rounded-xl border border-violet-500/20 overflow-hidden">
                      <div className="bg-violet-500/10 px-4 py-3 border-b border-violet-500/10">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-bold text-violet-400 flex items-center gap-2">
                            <span>💎</span> Hidden Gems
                          </h4>
                          <span className="text-xs bg-violet-500/20 text-violet-400 px-2 py-0.5 rounded-full font-semibold">
                            {insightsData.hiddenGems.length}
                          </span>
                        </div>
                        <p className="text-[10px] text-zinc-500 mt-1">Excellent efficiency, underexploited</p>
                      </div>
                      <div className="p-3 space-y-2 max-h-52 overflow-y-auto custom-scrollbar">
                        {insightsData.hiddenGems.length > 0 ? insightsData.hiddenGems.map((camp, i) => (
                          <div key={i} className="bg-violet-500/5 border border-violet-500/10 rounded-lg p-2.5">
                            <div className="flex items-center justify-between">
                              <p className="text-xs font-medium text-white truncate flex-1">{camp.name}</p>
                              <span className="px-2 py-0.5 bg-violet-500/20 text-violet-400 rounded text-[10px] font-bold ml-2">
                                {camp.efficiencyRating}x better
                              </span>
                            </div>
                            <p className="text-[10px] text-zinc-500 truncate">{camp.clientName}</p>
                            <div className="flex items-center justify-between mt-1.5">
                              <span className="text-[10px] text-violet-400 font-bold">CPR: {formatCurrency(camp.cpr)}</span>
                              <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${camp.scalePotential === 'High' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                                {camp.scalePotential} potential
                              </span>
                            </div>
                          </div>
                        )) : (
                          <p className="text-xs text-zinc-500 text-center py-4">No hidden gems found</p>
                        )}
                      </div>
                    </div>

                    {/* Budget Misallocated - NEW */}
                    <div className="bg-white/5 rounded-xl border border-amber-500/20 overflow-hidden">
                      <div className="bg-amber-500/10 px-4 py-3 border-b border-amber-500/10">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-bold text-amber-400 flex items-center gap-2">
                            <span>⚖️</span> Budget Misallocated
                          </h4>
                          <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full font-semibold">
                            {insightsData.budgetMisallocated.length}
                          </span>
                        </div>
                        <p className="text-[10px] text-zinc-500 mt-1">High CPR getting more budget</p>
                      </div>
                      <div className="p-3 space-y-2 max-h-52 overflow-y-auto custom-scrollbar">
                        {insightsData.budgetMisallocated.length > 0 ? insightsData.budgetMisallocated.map((camp, i) => (
                          <div key={i} className="bg-amber-500/5 border border-amber-500/10 rounded-lg p-2.5">
                            <p className="text-xs font-medium text-white truncate">{camp.name}</p>
                            <p className="text-[10px] text-zinc-500 truncate">{camp.clientName}</p>
                            <div className="flex items-center justify-between mt-1.5">
                              <span className="text-[10px] text-amber-400 font-bold">CPR: {formatCurrency(camp.cpr)}</span>
                              <span className="text-[10px] text-red-400">~{formatCurrency(camp.wastedAmount)} wasted</span>
                            </div>
                          </div>
                        )) : (
                          <div className="text-center py-4">
                            <svg className="w-6 h-6 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            <p className="text-xs text-emerald-400 mt-2">Budget allocation looks good!</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Second Row: Message Opportunity & Top Spenders */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Message Volume Opportunity - NEW */}
                    <div className="bg-white/5 rounded-xl border border-green-500/20 overflow-hidden">
                      <div className="bg-green-500/10 px-4 py-3 border-b border-green-500/10">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-bold text-green-400 flex items-center gap-2">
                            <span>💬</span> WhatsApp Scale Opportunity
                          </h4>
                          <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full font-semibold">
                            {insightsData.messageOpportunity.length}
                          </span>
                        </div>
                        <p className="text-[10px] text-zinc-500 mt-1">Low CPMC campaigns ready to scale</p>
                      </div>
                      <div className="p-3 space-y-2 max-h-52 overflow-y-auto custom-scrollbar">
                        {insightsData.messageOpportunity.length > 0 ? insightsData.messageOpportunity.map((camp, i) => (
                          <div key={i} className="bg-green-500/5 border border-green-500/10 rounded-lg p-2.5">
                            <div className="flex items-center justify-between">
                              <p className="text-xs font-medium text-white truncate flex-1">{camp.name}</p>
                              <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ml-2 ${camp.scaleRoom === 'High' ? 'bg-emerald-500/20 text-emerald-400' : camp.scaleRoom === 'Medium' ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                                {camp.scaleRoom}
                              </span>
                            </div>
                            <p className="text-[10px] text-zinc-500 truncate">{camp.clientName}</p>
                            <div className="flex items-center justify-between mt-1.5">
                              <span className="text-[10px] text-green-400 font-bold">CPMC: {formatCurrency(camp.cpmc)}</span>
                              <span className="text-[10px] text-zinc-400">{camp.results} conversations</span>
                            </div>
                          </div>
                        )) : (
                          <p className="text-xs text-zinc-500 text-center py-4">No WhatsApp campaigns to scale</p>
                        )}
                      </div>
                    </div>

                    {/* Top Spending Accounts */}
                    <div className="bg-white/5 rounded-xl border border-[#1877F2]/20 overflow-hidden">
                      <div className="bg-[#1877F2]/10 px-4 py-3 border-b border-[#1877F2]/10">
                        <h4 className="text-sm font-bold text-white flex items-center gap-2">
                          <span>💰</span> Top Spending Accounts
                        </h4>
                        <p className="text-[10px] text-zinc-500 mt-1">Highest spenders with performance status</p>
                      </div>
                      <div className="p-3 space-y-2 max-h-52 overflow-y-auto custom-scrollbar">
                        {insightsData.spendByClient.slice(0, 5).map((client, i) => {
                          const isGood = client.score >= 70;
                          const isWarning = client.score >= 40 && client.score < 70;
                          return (
                            <div key={i} className="bg-white/5 rounded-lg p-2.5 hover:bg-white/10 transition-colors cursor-pointer"
                                 onClick={() => setExpandedClient(client.id)}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                                    i === 0 ? 'bg-amber-500/30 text-amber-300' : 'bg-white/10 text-zinc-500'
                                  }`}>
                                    {i + 1}
                                  </span>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-xs font-medium text-white truncate">{client.fullName}</p>
                                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                                      isGood ? 'bg-emerald-500/20 text-emerald-400' :
                                      isWarning ? 'bg-amber-500/20 text-amber-400' :
                                      'bg-red-500/20 text-red-400'
                                    }`}>
                                      {isGood ? 'Good' : isWarning ? 'Warning' : 'Critical'}
                                    </span>
                                  </div>
                                </div>
                                <p className="text-sm font-bold text-white">{formatCurrency(client.spend)}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ==================== CATEGORY 5: COST EFFICIENCY ==================== */}
            <div className="bg-[#09090B] rounded-2xl border border-cyan-500/20 overflow-hidden">
              <button
                onClick={() => toggleCategory('efficiency')}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-cyan-500/20 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#6366f1]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                  </div>
                  <div className="text-left">
                    <h3 className="text-base font-bold text-cyan-400">Cost Efficiency & Performance</h3>
                    <p className="text-xs text-zinc-500">CPM, CPC outliers & top performers</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex gap-2">
                    {insightsData.topEfficient.length > 0 && (
                      <span className="px-2.5 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-xs font-semibold">
                        {insightsData.topEfficient.length} top performers
                      </span>
                    )}
                    {insightsData.cpmWatch.length > 0 && (
                      <span className="px-2.5 py-1 bg-orange-500/20 text-orange-400 rounded-full text-xs font-semibold">
                        {insightsData.cpmWatch.length} high CPM
                      </span>
                    )}
                    {insightsData.cpcOutliers.length > 0 && (
                      <span className="px-2.5 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-semibold">
                        {insightsData.cpcOutliers.length} CPC outliers
                      </span>
                    )}
                  </div>
                  <svg className={`w-5 h-5 text-zinc-400 transition-transform ${expandedCategories.efficiency ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              
              {expandedCategories.efficiency && (
                <div className="px-5 pb-5 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Top Performers */}
                    <div className="bg-white/5 rounded-xl border border-emerald-500/20 overflow-hidden">
                      <div className="bg-emerald-500/10 px-4 py-3 border-b border-emerald-500/10">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-bold text-emerald-400 flex items-center gap-2">
                            <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg> Top Performers
                          </h4>
                          <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full font-semibold">
                            {insightsData.topEfficient.length}
                          </span>
                        </div>
                        <p className="text-[10px] text-zinc-500 mt-1">Lowest CPR campaigns</p>
                      </div>
                      <div className="p-3 space-y-2 max-h-52 overflow-y-auto custom-scrollbar">
                        {insightsData.topEfficient.slice(0, 5).map((camp, i) => (
                          <div key={i} className="flex items-center justify-between bg-white/5 rounded-lg p-2.5 hover:bg-white/10 transition-colors">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                                i === 0 ? 'bg-amber-500/30 text-amber-300' : 
                                i === 1 ? 'bg-zinc-400/30 text-zinc-300' : 
                                i === 2 ? 'bg-orange-600/30 text-orange-300' : 
                                'bg-white/10 text-zinc-500'
                              }`}>
                                {i + 1}
                              </span>
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-medium text-white truncate">{camp.name}</p>
                                <p className="text-[10px] text-zinc-500 truncate">{camp.clientName}</p>
                              </div>
                            </div>
                            <p className="text-sm font-bold text-emerald-400">{formatCurrency(camp.cpr)}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* CPM Watch - NEW */}
                    <div className="bg-white/5 rounded-xl border border-orange-500/20 overflow-hidden">
                      <div className="bg-orange-500/10 px-4 py-3 border-b border-orange-500/10">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-bold text-orange-400 flex items-center gap-2">
                            <svg className="w-4 h-4 text-[#6366f1]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg> CPM Watch
                          </h4>
                          <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded-full font-semibold">
                            {insightsData.cpmWatch.length}
                          </span>
                        </div>
                        <p className="text-[10px] text-zinc-500 mt-1">High CPMs - audience saturation</p>
                      </div>
                      <div className="p-3 space-y-2 max-h-52 overflow-y-auto custom-scrollbar">
                        {insightsData.cpmWatch.length > 0 ? insightsData.cpmWatch.map((camp, i) => (
                          <div key={i} className={`rounded-lg p-2.5 ${camp.severity === 'critical' ? 'bg-red-500/10 border border-red-500/20' : 'bg-orange-500/10 border border-orange-500/20'}`}>
                            <p className="text-xs font-medium text-white truncate">{camp.name}</p>
                            <p className="text-[10px] text-zinc-500 truncate">{camp.clientName}</p>
                            <div className="flex items-center justify-between mt-1.5">
                              <span className="text-[10px] text-orange-400 font-bold">CPM: {formatCurrency(camp.cpm)}</span>
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${camp.severity === 'critical' ? 'bg-red-500/20 text-red-400' : 'bg-orange-500/20 text-orange-400'}`}>
                                +{camp.cpmIncrease}%
                              </span>
                            </div>
                          </div>
                        )) : (
                          <div className="text-center py-4">
                            <svg className="w-6 h-6 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            <p className="text-xs text-emerald-400 mt-2">CPMs are normal!</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* CPC Outliers - NEW */}
                    <div className="bg-white/5 rounded-xl border border-red-500/20 overflow-hidden">
                      <div className="bg-red-500/10 px-4 py-3 border-b border-red-500/10">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-bold text-red-400 flex items-center gap-2">
                            <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg> CPC Outliers
                          </h4>
                          <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full font-semibold">
                            {insightsData.cpcOutliers.length}
                          </span>
                        </div>
                        <p className="text-[10px] text-zinc-500 mt-1">Abnormal cost-per-click</p>
                      </div>
                      <div className="p-3 space-y-2 max-h-52 overflow-y-auto custom-scrollbar">
                        {insightsData.cpcOutliers.length > 0 ? insightsData.cpcOutliers.map((camp, i) => (
                          <div key={i} className="bg-red-500/5 border border-red-500/10 rounded-lg p-2.5">
                            <p className="text-xs font-medium text-white truncate">{camp.name}</p>
                            <p className="text-[10px] text-zinc-500 truncate">{camp.clientName}</p>
                            <div className="flex items-center justify-between mt-1.5">
                              <span className="text-[10px] text-red-400 font-bold">CPC: {formatCurrency(camp.cpc)}</span>
                              <span className="text-[10px] text-zinc-400">+{camp.cpcIncrease}% vs avg</span>
                            </div>
                            <p className="text-[10px] text-amber-400 mt-1">~{formatCurrency(camp.potentialSavings)} potential savings</p>
                          </div>
                        )) : (
                          <div className="text-center py-4">
                            <svg className="w-6 h-6 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            <p className="text-xs text-emerald-400 mt-2">All CPCs normal!</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ROAS Leaders Row */}
                  {insightsData.roasLeaders.length > 0 && (
                    <div className="grid grid-cols-1">
                      <div className="bg-white/5 rounded-xl border border-emerald-500/20 overflow-hidden">
                        <div className="bg-emerald-500/10 px-4 py-3 border-b border-emerald-500/10">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-bold text-emerald-400 flex items-center gap-2">
                              <span>🛒</span> ROAS Leaders (Sales Campaigns)
                            </h4>
                            <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full font-semibold">
                              {insightsData.roasLeaders.length}
                            </span>
                          </div>
                          <p className="text-[10px] text-zinc-500 mt-1">Best return on ad spend</p>
                        </div>
                        <div className="p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                          {insightsData.roasLeaders.map((camp, i) => (
                            <div key={i} className="bg-emerald-500/5 border border-emerald-500/10 rounded-lg p-2.5">
                              <div className="flex items-center justify-between">
                                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                                  i === 0 ? 'bg-amber-500/30 text-amber-300' : 'bg-white/10 text-zinc-500'
                                }`}>
                                  {i + 1}
                                </span>
                                <span className="text-lg font-bold text-emerald-400">{camp.roasValue?.toFixed(2)}x</span>
                              </div>
                              <p className="text-xs font-medium text-white truncate mt-1">{camp.name}</p>
                              <p className="text-[10px] text-zinc-500">{camp.clientName}</p>
                              <div className="flex items-center justify-between mt-1.5">
                                <span className="text-[10px] text-zinc-400">{formatCurrency(camp.spend)} spent</span>
                                <span className="text-[10px] text-emerald-400">{formatCurrency(camp.revenue)} revenue</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Account Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              value={accountSearch}
              onChange={e => setAccountSearch(e.target.value)}
              placeholder="Search accounts..."
              className="bg-[#09090B] border border-white/10 rounded-lg px-3 py-2 pl-9 text-sm text-white placeholder-white/40 w-full focus:outline-none focus:border-white/20"
            />
            {accountSearch && (
              <button onClick={() => setAccountSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2">
            {[
              { id: 'all', label: 'All Clients', count: platformClients.length },
              { id: 'good', label: 'Good', count: summaryStats.goodClients, color: 'text-emerald-400' },
              { id: 'warning', label: 'Warning', count: summaryStats.warningClients, color: 'text-amber-400' },
              { id: 'critical', label: 'Critical', count: summaryStats.criticalClients, color: 'text-red-400' }
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilterStatus(f.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5 ${
                  filterStatus === f.id
                    ? 'bg-[#1877F2]/20 text-[#1877F2] border border-[#1877F2]/30'
                    : 'bg-white/5 text-zinc-400 hover:text-white border border-transparent'
                }`}
              >
                <span className={f.color}>{f.label}</span>
                <span className="px-1.5 py-0.5 rounded bg-white/10 text-[10px]">{f.count}</span>
              </button>
            ))}
          </div>

          {/* Client Cards - Already sorted by spend descending via platformClients */}
          <div className="space-y-4">
            {filteredClients.map(client => (
              <ClientCard
                key={client.id}
                client={client}
                campaigns={client.campaigns || []}
                isExpanded={expandedClient === client.id}
                onToggle={() => setExpandedClient(expandedClient === client.id ? null : client.id)}
                onAnalyze={() => setAiAnalysisClient({ id: client.id, name: client.name })}
                addToast={addToast}
              />
            ))}

            {!loading && platformClients.length === 0 && (
              <div className="text-center py-12 text-zinc-400">No Meta accounts found</div>
            )}

            {filteredClients.length === 0 && platformClients.length > 0 && (
              <div className="text-center py-16 bg-[#09090B] rounded-2xl border border-white/10">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/5 flex items-center justify-center">
                  <svg className="w-8 h-8 text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">No Clients Found</h3>
                <p className="text-zinc-500">No Meta accounts match the current filter.</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Health Tab */}
      {activeTab === 'health' && (
        <div className="space-y-6">
          {/* Health Summary Banner */}
          <div className="bg-gradient-to-r from-emerald-500/10 via-[#0D0D0F] to-transparent rounded-2xl border border-emerald-500/20 p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                  <svg className="w-7 h-7 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Meta Accounts Health</h2>
                  <p className="text-zinc-400 text-sm">AI-powered analysis of your ad accounts</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="bg-emerald-500/10 rounded-xl px-4 py-2 text-center">
                  <p className="text-2xl font-bold text-emerald-400">{healthData?.healthy?.length || 0}</p>
                  <p className="text-[10px] text-zinc-500 uppercase">Healthy</p>
                </div>
                <div className="bg-amber-500/10 rounded-xl px-4 py-2 text-center">
                  <p className="text-2xl font-bold text-amber-400">{healthData?.warning?.length || 0}</p>
                  <p className="text-[10px] text-zinc-500 uppercase">Warning</p>
                </div>
                <div className="bg-red-500/10 rounded-xl px-4 py-2 text-center">
                  <p className="text-2xl font-bold text-red-400">{healthData?.critical?.length || 0}</p>
                  <p className="text-[10px] text-zinc-500 uppercase">Critical</p>
                </div>
              </div>
            </div>
          </div>

          {/* Critical Accounts */}
          {healthData?.critical?.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-red-400 flex items-center gap-2">
                <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg> Critical - Needs Immediate Attention ({healthData.critical.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {healthData.critical.map(account => (
                  <div key={account.id} className="bg-red-500/5 border border-red-500/20 rounded-xl p-4 hover:border-red-500/40 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-white">{account.name}</h4>
                        <p className="text-xs text-zinc-500">Health Score: {account.health_score}%</p>
                      </div>
                      <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded text-[10px] font-bold">CRITICAL</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      <div className="bg-white/5 rounded-lg p-2 text-center">
                        <p className="text-[9px] text-zinc-500 uppercase">7d Spend</p>
                        <p className="text-xs font-bold text-white font-mono">{account.spend_7d > 0 ? formatCurrency(account.spend_7d) : '—'}</p>
                      </div>
                      <div className="bg-white/5 rounded-lg p-2 text-center">
                        <p className="text-[9px] text-zinc-500 uppercase">7d CPR</p>
                        <p className="text-xs font-bold text-red-400 font-mono">{account.cpr_7d ? formatCurrency(account.cpr_7d) : '—'}</p>
                      </div>
                      <div className="bg-white/5 rounded-lg p-2 text-center">
                        <p className="text-[9px] text-zinc-500 uppercase">Critical</p>
                        <p className="text-xs font-bold text-red-400 font-mono">{account.critical_campaigns || 0} camps</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Warning Accounts */}
          {healthData?.warning?.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-amber-400 flex items-center gap-2">
                <svg className="w-4 h-4 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg> Warning - Requires Review ({healthData.warning.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {healthData.warning.map(account => (
                  <div key={account.id} className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 hover:border-amber-500/40 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-white">{account.name}</h4>
                        <p className="text-xs text-zinc-500">Health Score: {account.health_score}%</p>
                      </div>
                      <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-[10px] font-bold">WARNING</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      <div className="bg-white/5 rounded-lg p-2 text-center">
                        <p className="text-[9px] text-zinc-500 uppercase">7d Spend</p>
                        <p className="text-xs font-bold text-white font-mono">{account.spend_7d > 0 ? formatCurrency(account.spend_7d) : '—'}</p>
                      </div>
                      <div className="bg-white/5 rounded-lg p-2 text-center">
                        <p className="text-[9px] text-zinc-500 uppercase">7d CPR</p>
                        <p className="text-xs font-bold text-amber-400 font-mono">{account.cpr_7d ? formatCurrency(account.cpr_7d) : '—'}</p>
                      </div>
                      <div className="bg-white/5 rounded-lg p-2 text-center">
                        <p className="text-[9px] text-zinc-500 uppercase">Results</p>
                        <p className="text-xs font-bold text-amber-400 font-mono">{account.results_7d || 0}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Healthy Accounts */}
          {healthData?.healthy?.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-emerald-400 flex items-center gap-2">
                <svg className="w-4 h-4 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> Healthy ({healthData.healthy.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {healthData.healthy.slice(0, 9).map(account => (
                  <div key={account.id} className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-white truncate">{account.name}</h4>
                      <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-[10px] font-bold shrink-0 ml-2">{account.health_score}%</span>
                    </div>
                    <div className="flex items-center gap-3 text-[10px] text-zinc-500">
                      <span>{account.spend_7d > 0 ? formatCurrency(account.spend_7d) : '—'} spend</span>
                      {account.results_7d > 0 && <span>• {account.results_7d} results</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {(!healthData || healthData.total === 0) && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-8 text-center">
              <p className="text-zinc-400">No health data available yet. Run sync to get health analysis.</p>
            </div>
          )}
        </div>
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <div className="space-y-6">
          {/* Alerts Header */}
          <div className="bg-gradient-to-r from-amber-500/10 via-[#0D0D0F] to-transparent rounded-2xl border border-amber-500/20 p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-amber-500/20 rounded-xl flex items-center justify-center">
                  <svg className="w-7 h-7 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Meta Alerts</h2>
                  <p className="text-zinc-400 text-sm">
                    {alerts.length} alerts • {alertsUnreadCount} unread
                  </p>
                </div>
              </div>
              <button
                onClick={fetchAlerts}
                className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm text-white transition-colors"
              >
                Refresh
              </button>
            </div>
          </div>

          {/* Alerts List */}
          {alerts.length > 0 ? (
            <div className="space-y-3">
              {alerts.map(alert => {
                const severityConfig = {
                  critical: { bg: 'bg-red-500/5', border: 'border-red-500/20', badge: 'bg-red-500/20 text-red-400' },
                  high: { bg: 'bg-orange-500/5', border: 'border-orange-500/20', badge: 'bg-orange-500/20 text-orange-400' },
                  medium: { bg: 'bg-amber-500/5', border: 'border-amber-500/20', badge: 'bg-amber-500/20 text-amber-400' },
                  low: { bg: 'bg-blue-500/5', border: 'border-blue-500/20', badge: 'bg-blue-500/20 text-blue-400' }
                }[alert.severity] || { bg: 'bg-white/5', border: 'border-white/10', badge: 'bg-white/10 text-white' };

                return (
                  <div 
                    key={alert.id} 
                    className={`${severityConfig.bg} border ${severityConfig.border} rounded-xl p-4 ${!alert.is_read ? 'ring-1 ring-amber-500/30' : ''}`}
                    onClick={() => !alert.is_read && markAlertAsRead(alert.id)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${severityConfig.badge}`}>
                            {alert.severity}
                          </span>
                          <span className="px-2 py-0.5 bg-white/10 text-zinc-400 rounded text-[10px]">
                            {alert.alert_type?.replace(/_/g, ' ')}
                          </span>
                          {!alert.is_read && (
                            <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></span>
                          )}
                        </div>
                        <h4 className="text-sm font-semibold text-white mb-1">{alert.title}</h4>
                        <p className="text-xs text-zinc-400">{alert.message}</p>
                        <p className="text-[10px] text-zinc-600 mt-2">
                          {alert.account_name} • {new Date(alert.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      {!alert.is_resolved && (
                        <button
                          onClick={(e) => { e.stopPropagation(); resolveAlert(alert.id); }}
                          className="px-3 py-1.5 bg-emerald-500/20 text-emerald-400 rounded-lg text-xs font-semibold hover:bg-emerald-500/30 transition-colors"
                        >
                          Resolve
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-8 text-center">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-emerald-400 mb-2">All Clear!</h3>
              <p className="text-zinc-400">No alerts for Meta accounts.</p>
            </div>
          )}
        </div>
      )}

      {/* Inactive Tab */}
      {activeTab === 'inactive' && (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-red-500/10 to-transparent rounded-2xl border border-red-500/20 p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-red-500/20 rounded-xl flex items-center justify-center">
                <svg className="w-7 h-7 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Inactive Accounts</h2>
                <p className="text-zinc-400 text-sm">
                  {inactiveAccounts.length} accounts stopped spending and may need recharge
                </p>
              </div>
            </div>
          </div>

          {inactiveAccounts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {inactiveAccounts.map(account => (
                <InactiveAccountCard key={account.account_id || account.id} account={account} />
              ))}
            </div>
          ) : (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-8 text-center">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-emerald-400 mb-2">All Accounts Active!</h3>
              <p className="text-zinc-400">No recently inactive accounts found.</p>
            </div>
          )}
        </div>
      )}

      {/* AI Analysis Modal */}
      <AIAnalysisPanel
        accent="#1877F2"
        clientId={aiAnalysisClient?.id}
        clientName={aiAnalysisClient?.name}
        isOpen={!!aiAnalysisClient}
        onClose={() => setAiAnalysisClient(null)}
      />
    </div>
  );
};

export default MetaPerformance;
